import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  DataLayerService,
  GmSltService,
  GmTireCentralService,
  ServiceVisitRecordService,
} from '@applications/sha-services';
import { Actions } from '@ngrx/effects';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { GMServiceVisitByDealerQueryParameters, GMServiceVisitByVinQueryParameters } from '@quorum/models/oem-query';
import { GMServiceVisitByDealer, GMServiceVisitByVin } from '@quorum/models/oem-resource';
import { GmSltData, ServiceVisitRecord } from '@quorum/models/xs-misc';
import {
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  CampaignHeaderQueryParameters,
  QuoteDetailQueryParameters,
  WorkOrderQueryParameters,
} from '@quorum/models/xs-query';
import {
  Address,
  ServiceAppointment as Appointment,
  AppointmentCase,
  Associate,
  CampaignHeader,
  CombinedTax,
  Employee,
  MessageTransactionMessagePreference,
  QuoteDetail,
  Vehicle,
  WorkOrder,
  WorkorderDeclinedMpviCode,
} from '@quorum/models/xs-resource';
import { OemMenuService } from '@quorum/quorum-api';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import {
  UpdateAppointmentOnServerError,
  UpdateAppointmentOnServerSuccess,
} from '@quorum/sha-entities/fixed-operations';
import { MediaStateService } from '@quorum/sha-media';
import { RouterStateService } from '@quorum/sha-router';
import { status, SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { InspectionService } from '@quorum/xsr-legacy-vip/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { VehicleMaintenanceStateService } from '@quorum/xsr-vehicle-maintenance/services';
import { HyundaiMenuNgrxFacade } from 'libs/hyundai-menu-ngrx/src/lib/+state/hyundai-menu-ngrx.facade';
import { combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AcceptQuoteLineDialogComponent } from '../accept-quote-line-dialog/accept-quote-line-dialog.component';
import { AppointmentConfirmationDialogComponent } from '../appointment-confirmation-dialog/appointment-confirmation-dialog.component';

@Component({
  selector: 'svc-appointment',
  templateUrl: './appointment.component.html',
  styleUrls: ['./appointment.component.scss'],
})
export class AppointmentComponent implements OnInit, OnDestroy {
  appointmentId: number;
  vehicleId: number;
  caseListView = 'list';
  caseToEdit: AppointmentCase = null;
  actionSub: Subscription;
  updateSub: Subscription;
  createSub: Subscription;
  quoteLineSub: Subscription;
  updateAssociateSub: Subscription;
  updateAddressSub: Subscription;
  updateSelectedAppointmentSub: Subscription;
  private updateSelectedAppointmentErrorSub: Subscription;
  deleteSelectedAppointmentCaseSub: Subscription;
  updateQuoteLineSub: Subscription;
  updateVehicleSub: Subscription;
  currentEmployee: Employee;
  addQuoteLineSub: Subscription;
  workOrderDeclinedOperations: WorkorderDeclinedMpviCode[] = [];
  serviceAppointmentDropdowns$: Observable<any>;
  primaryRouteUrl: string;
  // start with verify required and set to false once response comes back from API
  verifyCustomerRequired = true;
  associateForm: FormGroup;
  addressForm: FormGroup;
  quoteLinesFromMenu$: Observable<any>;
  dashboardSubscription$: Subscription;
  combinedAppointmentSubscription$: Subscription;
  servicesFromMenuSubscription$: Subscription;
  removeAcceptedServiceFromHyundaiMenuSubscription$: Subscription;
  declinedFromMenuSubscription$: Subscription;
  removeDeclinedServiceFromHyundaiMenuSubscription$: Subscription;
  updateDeclinedServiceFromHyundaiMenuSubscription$: Subscription;
  dashboardRetrievalSubscription$: Subscription;
  routerStateSubscription$: Subscription;
  inspectionSavedNotificationSubscription$: Subscription;
  scheduleSavedNotificationSubscription$: Subscription;
  createReservedWorkOrderSubscription$: Subscription;
  authenticatedEmployeeSubscription$: Subscription;
  campaignsToCreate$: ReplaySubject<QuoteDetail[]> = new ReplaySubject<QuoteDetail[]>();
  campaigns$: Observable<QuoteDetail[]>;
  editMode = false;
  visitSub$: Subscription;
  oem: string;
  createQuoteLineOnServer$: Subscription;
  hyundaiIntegrationEnabled$: Observable<{ integrationEnabled: string; menuEnabled: string }>;
  vehicleDiagnostics$: Observable<{ enabled: boolean; dealerCountryCode: string }>;
  campaignNumbers: string[] = [];
  selectedAppointmentCasesSub: Subscription;
  workOrderSub$: Subscription;
  destroy$ = new Subject();
  gmSltData$: Observable<GmSltData>;
  serviceVisitRecord$: Observable<ServiceVisitRecord>;
  toggleTireFinderWidget = false;
  quoteLines$: Observable<QuoteDetail[]>;
  maintenanceScheduleSource$: Observable<string>;
  private checkInOdometerEdited = false;
  private checkInSavePending = false;
  private pendingServiceVisitId: string = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    public serviceLaneFacade: ServiceLaneFacade,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    public dialog: MatDialog,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    private routerStateService: RouterStateService,
    public inspectionService: InspectionService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private vehicleMaintenanceService: VehicleMaintenanceStateService,
    private mediaStateService: MediaStateService,
    private dataLayerService: DataLayerService,
    public hyundaiMenuFacade: HyundaiMenuNgrxFacade,
    private oemMenuService: OemMenuService,
    public actions$: Actions,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade,
    private gmSltService: GmSltService,
    private gmTireCentralService: GmTireCentralService,
    private serviceVisitRecordService: ServiceVisitRecordService
  ) {}

  ngOnInit() {
    /**
     * TODO: This block would ideally live on the gm-slt service but
     * due to circular dependency problems within the repo it is not advised
     * (also found on inspection/menu component).
     */
    this.gmSltData$ = combineLatest([
      this.inspectionService.workOrderId$.pipe(
        filter((workOrderId) => workOrderId),
        take(1)
      ),
      this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser != null),
        map((authUser) => authUser.user),
        take(1)
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        switchMap((employee) => {
          return this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
            map((oemRooftopLink) => {
              return oemRooftopLink?.associateId ?? null;
            })
          );
        }),
        take(1)
      ),
    ]).pipe(
      switchMap(([workOrderId, authenticatedUser, oem]) => {
        if (oem === 'GM') {
          return this.gmSltService.getOrCreateGmSltData(authenticatedUser.destination, workOrderId);
        } else {
          return of(null);
        }
      })
    );

    this.serviceVisitRecord$ = combineLatest([
      this.inspectionService.workOrderId$.pipe(
        filter((workOrderId) => workOrderId),
        take(1)
      ),
      this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser != null),
        map((authUser) => authUser.user),
        take(1)
      ),
    ]).pipe(
      switchMap(([workOrderId, authenticatedUser]) => {
        return this.serviceVisitRecordService.getOrCreateServiceVisitRecord(
          authenticatedUser.destination,
          this.activatedRoute.snapshot.params.appointmentId?.toString(),
          workOrderId?.toString()
        );
      }),
      switchMap((svr: ServiceVisitRecord) => {
        if (svr.communications?.advisorMobileCheckIn) {
          return of(svr);
        }
        return this.serviceVisitRecordService.recordAdvisorMobileCheckIn(svr);
      }),
      switchMap((svr: ServiceVisitRecord) => {
        return this.serviceVisitRecordService.recordInitialSnapshot(svr);
      }),
      shareReplay(1)
    );

    this.hyundaiIntegrationEnabled$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((emp) => emp != null),
      take(1),
      switchMap((employee) => {
        return this.systemControlStateService
          .getSystemControlValues(
            ['CL_HYUNDAI_INTEGRATION_ENABLED', 'CL_HYUNDAI_MENU_INTEGRATION_ENABLED'],
            employee.rooftopId
          )
          .pipe(
            take(1),
            map((scts) => {
              return {
                integrationEnabled: scts['CL_HYUNDAI_INTEGRATION_ENABLED'].value,
                menuEnabled: scts['CL_HYUNDAI_MENU_INTEGRATION_ENABLED'].value,
              };
            })
          );
      })
    );

    this.vehicleDiagnostics$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((emp) => emp != null),
      take(1),
      switchMap((employee) => {
        return this.systemControlStateService
          .getSystemControlValues(
            ['CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED', 'CL_DEALER_COUNTRY_ID'],
            employee.rooftopId
          )
          .pipe(
            take(1),
            map((scts) => {
              return {
                enabled: scts['CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED'].value === '1' ? true : false,
                dealerCountryCode:
                  scts['CL_DEALER_COUNTRY_ID'].value === 'CAN'
                    ? 'CA'
                    : scts['CL_DEALER_COUNTRY_ID'].value === 'USA'
                    ? 'US'
                    : 'US',
              };
            })
          );
      })
    );

    this.visitStuff();

    this.addQuoteLineSub = this.serviceLaneFacade.addQuoteToState$.subscribe();
    this.actionSub = this.serviceLaneFacade.setSelectedAppointmentCases$.subscribe();
    this.updateSub = this.serviceLaneFacade.updateSelectedAppointmentCase$.subscribe();
    this.createSub = this.serviceLaneFacade.createAppointmentCase$.subscribe();
    this.updateSelectedAppointmentSub = this.serviceLaneFacade.updateSelectedAppointment$
      .pipe(takeUntil(this.destroy$))
      .subscribe((action: UpdateAppointmentOnServerSuccess) => this.handleCheckInSaveSuccess(action.payload));
    this.updateSelectedAppointmentErrorSub = this.serviceLaneFacade.updateSelectedAppointmentError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((action: UpdateAppointmentOnServerError) => this.handleCheckInSaveError(action.payload.appointment));
    this.deleteSelectedAppointmentCaseSub = this.serviceLaneFacade.deleteSelectedAppointmentCase$.subscribe();
    this.updateAssociateSub = this.serviceLaneFacade.updateAssociate$.subscribe();
    this.updateAddressSub = this.serviceLaneFacade.updateAddress$.subscribe();
    this.updateQuoteLineSub = this.serviceLaneFacade.updateQuoteLine$.subscribe();
    this.appointmentId = this.activatedRoute.snapshot.params.appointmentId;
    this.vehicleId = this.activatedRoute.snapshot.params.vehicleId;
    this.updateVehicleSub = this.serviceLaneFacade.updateVehicle$.subscribe();
    this.quoteLineSub = this.serviceLaneFacade.addCreatedQuoteLineToState$.subscribe();
    this.serviceLaneFacade.warrantyNotifications$.next(false);

    this.combinedAppointmentSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1),
        switchMap((employee) => {
          return this.systemControlStateService
            .getSystemControlValues(['CL_COMMUNICATOR_CUST_INFO_PROMPT_SERVICE_APPT'], employee.rooftopId)
            .pipe(
              take(1),
              map((scts) => {
                return scts['CL_COMMUNICATOR_CUST_INFO_PROMPT_SERVICE_APPT'].value;
              })
            );
        })
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(filter((appointment) => appointment != null)),
    ])
      .pipe(
        map(([requireCustomerVerification, appointment]) => {
          return { requireCustomerVerification, appointment };
        }),
        take(1)
      )

      .subscribe((data) => {
        if (data.requireCustomerVerification == '1' && !data.appointment.customerVerified) {
          this.verifyCustomerRequired = true;

          combineLatest([
            this.serviceLaneFacade.customerAssociate$.pipe(filter((associate) => associate != null)),
            this.serviceLaneFacade.customerAddress$.pipe(filter((address) => address != null)),
            this.serviceLaneDropdownFacade.serviceAppointmentDropdowns$,
            this.serviceLaneFacade.selectedAppointment$,
            this.serviceLaneFacade.vehicle$,
            this.inspectionService.workOrderId$.pipe(filter((workOrderId) => workOrderId !== null)),
            this.vehicleDiagnostics$,
          ])
            .pipe(
              map(
                ([
                  customerAssociate,
                  address,
                  serviceAppointmentDropdowns,
                  appointment,
                  vehicle,
                  workOrderId,
                  vehicleDiagnostics,
                ]) => {
                  return {
                    customerAssociate,
                    address,
                    serviceAppointmentDropdowns,
                    appointment,
                    vehicle,
                    workOrderId,
                    vehicleDiagnostics,
                  };
                }
              ),
              take(1)
            )
            .subscribe((data) => {
              const dialogRef = this.dialog.open(AppointmentConfirmationDialogComponent, {
                width: '80%',
                maxWidth: '624px',
                height: '70%',
                maxHeight: '752px',
                minHeight: '752px',
                data: {
                  address: data.address,
                  appointment: data.appointment,
                  customerAssociate: data.customerAssociate,
                  serviceAppointmentDropdowns: data.serviceAppointmentDropdowns,
                  vehicle: data.vehicle,
                  vehicleDiagnostics: data.vehicleDiagnostics,
                  oem: this.oem,
                },
                disableClose: true,
              });

              dialogRef.afterClosed().subscribe((result: any) => {
                if (result) {
                  this.checkInOdometerEdited = result.odometerEdited === true;
                  this.checkInSavePending = true;
                  this.verifyCustomer(
                    result.customerAssociate,
                    result.address,
                    result.appointment,
                    result.overrideOptions,
                    data.workOrderId,
                    result.odometerEdited
                  );
                } else {
                  this.skipVerifyCustomer();
                }
              });
            });
        } else {
          this.verifyCustomerRequired = false;
        }
      });
    //CL_COMMUNICATOR_CUST_INFO_PROMPT_SERVICE_APPT
    this.serviceAppointmentDropdowns$ = combineLatest(
      this.serviceLaneDropdownFacade.serviceAppointmentDropdowns$,
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1),
        switchMap((employee) => {
          return this.systemControlStateService
            .getSystemControlValues(['CL_GM_CADILLAC_SERVICE_CENTER'], employee.rooftopId)
            .pipe(startWith(null));
        })
      ),
      this.serviceLaneFacade.vehicle$.pipe(filter((vehicle) => vehicle != null))
    ).pipe(
      map(([dropdowns, sysControl, vehicle]) => {
        const dropdownsCopy = new DeepCopyPipe().transform(dropdowns);
        if (
          (sysControl && sysControl['CL_GM_CADILLAC_SERVICE_CENTER'].value !== '1') ||
          vehicle.make.toLowerCase() !== 'cadillac'
        ) {
          dropdownsCopy.transportOptions = dropdowns.transportOptions.filter(
            (transportOption) => !transportOption.name.toLowerCase().includes('cadillac premium concierge')
          );
        }
        return dropdownsCopy;
      })
    );

    this.selectedAppointmentCasesSub = this.serviceLaneFacade.selectedAppointmentCases$.subscribe(
      (selectedAppointmentCases) => {
        this.campaignNumbers = [];
        selectedAppointmentCases.forEach((selectedAppointmentCase) => {
          // if the appt case has a campaign number, add it to array to avoid duplicates
          if (
            selectedAppointmentCase.campaignNumber &&
            !this.campaignNumbers.includes(selectedAppointmentCase.campaignNumber)
          ) {
            this.campaignNumbers.push(selectedAppointmentCase.campaignNumber);
          }
        });

        // TESTING ONLY START
        // this.oem = 'NIS';
        // TESTING ONLY END

        switch (this.oem) {
          case 'GM':
            this.retrieveGMDashboardDataAndApplyRecalls();
            break;
          case 'FO':
            this.retrieveFordDashboardDataAndApplyRecalls();
            break;
          case 'TOY':
            this.retrieveToyotaDashboardDataAndApplyRecalls();
            break;
          case 'CH':
            this.retrieveChryslerDashboardDataAndApplyRecalls();
            break;
          case 'NIS':
            this.retrieveNissanDashboardDataAndApplyRecalls();
            break;
          case 'HYU':
            this.retrieveHyundaiDashboardDataAndApplyRecalls();
            break;
          default:
            break;
        }
      }
    );

    combineLatest([
      this.serviceLaneFacade.vehicle$.pipe(
        filter((vehicle) => vehicle !== null),
        take(1)
      ),
      this.serviceLaneFacade.workOrderId$.pipe(
        filter((workOrderId) => workOrderId !== null),
        take(1)
      ),
      this.serviceLaneFacade.dealerBac$.pipe(
        filter((dealerBac) => dealerBac !== null),
        take(1)
      ),
      this.serviceLaneFacade.oem$.pipe(
        filter((oem) => oem !== null),
        take(1),
        map((oem) => oem.associateId)
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1),
        switchMap((employee) => {
          return this.systemControlStateService.getSystemControlValues(
            ['CL_GM_TIRE_CENTRAL_WIDGET', 'CL_DEALER_COUNTRY_ID'],
            employee.rooftopId
          );
        })
      ),
    ])
      .pipe(
        take(1),
        tap(([vehicle, workOrderId, dealerBac, oem, systemControls]) => {
          const dealerCountryCode =
            systemControls['CL_DEALER_COUNTRY_ID'].value === 'CAN'
              ? 'CA'
              : systemControls['CL_DEALER_COUNTRY_ID'].value === 'USA'
              ? 'US'
              : 'US';
          if (oem === 'GM' && systemControls?.CL_GM_TIRE_CENTRAL_WIDGET?.value === '1') {
            this.gmTireCentralService.setupGMTireCentralIframe(vehicle, workOrderId, dealerBac, dealerCountryCode);
          }
        })
      )
      .subscribe();

    this.maintenanceScheduleSource$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((emp) => emp != null),
      take(1),
      switchMap((employee) => {
        return this.systemControlStateService
          .getSystemControlValues(['CL_MAINTENANCE_SCHEDULE_SOURCE'], employee.rooftopId)
          .pipe(
            take(1),
            map((scts) => {
              return (scts['CL_MAINTENANCE_SCHEDULE_SOURCE']?.value || 'Legacy').toUpperCase();
            })
          );
      })
    );
  }

  retrieveGMDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      //this.serviceLaneFacade.gmDashboardData$.pipe(
      this.serviceLaneFacade.oemDashboardData$.pipe(
        filter((gmDashboardData: any) => gmDashboardData != null),
        map((gmDashboardData: any) => {
          return gmDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, gmDashboardData, employee, appointmentId]) => {
          return { quoteId, gmDashboardData, employee, appointmentId };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('gmDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.gmDashboardData);
        if (data.gmDashboardData.dashboardJson != null) {
          // let dashboardCampaigns = data.gmDashboardData.dashboardJson.dashboardCampaigns.filter((dc: any) => {
          //   return dc.CampaignType == 'ZPSR';
          // });

          // get recalls from dashboard response
          const dashboardCampaigns = data.gmDashboardData.dashboardJson.dashboardAlerts.filter((dc: any) => {
            return dc.CampaignNumber != null;
          });

          // pass campaign numbers to campaign-headers route to see if these campaigns already exist in the DB
          const campaignQueryParams = new CampaignHeaderQueryParameters({
            id: dashboardCampaigns.map((campaign: any) => campaign.CampaignNumber).join(','),
          });
          const campaigns: Array<CampaignHeader> = [];
          this.apiService
            .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
            .pipe(take(1))
            .subscribe((databaseCampaigns: CampaignHeader[]) => {
              // check dashboard recalls against ones already in the DB to determine which ones are not in the DB
              const missingCampaignHeaders = dashboardCampaigns.filter((c1: any) =>
                databaseCampaigns.every((c2: CampaignHeader) => c2.id !== c1.CampaignNumber)
              );

              // if campaign (recall) exists in both dashboard response and DB, add to campaign array to be processed
              dashboardCampaigns.forEach((c1: any) => {
                databaseCampaigns.forEach((c2: CampaignHeader) => {
                  if (c1.CampaignNumber === c2.id) {
                    campaigns.push(c2);
                  }
                });
              });

              // if any campaigns (recalls) exist in dashboard response, but not in DB, POST missing to DB
              if (missingCampaignHeaders) {
                missingCampaignHeaders.forEach((campaign: any) => {
                  const campaignHeader: CampaignHeader = {
                    campaignJson: null,
                    chryslerFailureCode: null,
                    description: null,
                    estimatedTime: 0,
                    fordClaimTypesId: null,
                    fordConditionCodesId: null,
                    fordCustomerConcernCodesId: null,
                    id: campaign.CampaignNumber,
                    manufacturerId: 'GM',
                    name: campaign.CampaignDesc,
                    openFlag: '0',
                    operationCode: null,
                    recommendedHours: 0,
                    technicianGroupId: null,
                    technicianGroupName: null,
                    technicianId: null,
                    typeId: campaign.CampaignType,
                  };

                  this.apiService
                    .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                    .subscribe((campaign) => {
                      // add POSTed campaign to the array to be processed
                      campaigns.push(campaign);
                    });
                });
              }
            });
          const warrantyNotifications = data.gmDashboardData.dashboardJson.dashboardAlerts.filter((alert: any) => {
            return alert.CampaignGroup == 'Warranty Block' || alert.CampaignGroup == 'Branded Title';
          });

          if (warrantyNotifications.length > 0) {
            this.serviceLaneFacade.warrantyNotifications$.next(true);
          }

          const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters({ quoteHeaderId: data.quoteId });

          // get all quote lines currently on this quote
          this.apiService
            .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
            .pipe(take(1))
            .subscribe((quoteLines: QuoteDetail[]) => {
              const campaignQuotesToCreate: {
                quoteDetailForCampaign: QuoteDetail;
                campaignDate: string;
              }[] = [];

              // loop through campaigns (recalls) to be processed
              campaigns.forEach((campaign: CampaignHeader) => {
                // check to see if there is a line item already for this campaign number on this transaction
                if (
                  quoteLines.findIndex(
                    (ql: QuoteDetail) =>
                      ql.campaignNumber == campaign.id &&
                      ql.createdFromSourceReference == data.appointmentId &&
                      ql.createdFromSourceType == 'SA'
                  ) == -1
                ) {
                  // if not, create a new line item with the campaign details
                  const quoteDetail: QuoteDetail = new QuoteDetail();
                  quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                  quoteDetail.rooftopId = data.employee.rooftopId;
                  quoteDetail.employeeId = data.employee.associateId;
                  quoteDetail.associateId = data.employee.associateId;

                  quoteDetail.serviceName = campaign.name;
                  quoteDetail.campaignAddedBySystem = '1';
                  quoteDetail.campaignNumber = campaign.id;
                  quoteDetail.technicianGroupId = campaign.technicianGroupId;
                  quoteDetail.technicianGroupName = campaign.technicianGroupName;
                  quoteDetail.technicianId = campaign.technicianId;
                  quoteDetail.labourOpCode = campaign.operationCode;
                  quoteDetail.recommendedHours = campaign.recommendedHours;

                  quoteDetail.warrantySubmissionTypeId = 'CC';

                  quoteDetail.rateType = null;
                  quoteDetail.rate = 0;
                  quoteDetail.totalAmount = 0;
                  quoteDetail.expirationDate = new Date();
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

                  quoteDetail.quoteHeaderId = data.quoteId;
                  quoteDetail.createdFromSourceType = 'SA';
                  quoteDetail.createdFromSourceReference = data.appointmentId;

                  // check if the campaign number already exists on an appt case for this appt, if not, push to array to create
                  if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                    campaignQuotesToCreate.push({
                      quoteDetailForCampaign: quoteDetail,
                      campaignDate: dashboardCampaigns.find(
                        (dbCampaign: any) => dbCampaign.CampaignNumber === quoteDetail.campaignNumber
                      ).CampaignDate,
                    });
                  }
                }
              });

              // TESTING ONLY
              // const quoteDetail: QuoteDetail = new QuoteDetail();
              // quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
              // quoteDetail.rooftopId = data.employee.rooftopId;
              // quoteDetail.employeeId = data.employee.associateId;
              // quoteDetail.associateId = data.employee.associateId;

              // quoteDetail.serviceName = 'SOS, somehing on this thing is busted';
              // quoteDetail.campaignAddedBySystem = '1';
              // quoteDetail.campaignNumber = 'N18912345';
              // quoteDetail.technicianGroupId = null;
              // quoteDetail.technicianGroupName = null;
              // quoteDetail.technicianId = null;
              // quoteDetail.labourOpCode = null;
              // quoteDetail.recommendedHours = null;

              // quoteDetail.warrantySubmissionTypeId = 'CC';

              // quoteDetail.rateType = null;
              // quoteDetail.rate = 0;
              // quoteDetail.totalAmount = 0;
              // quoteDetail.expirationDate = new Date();
              // quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

              // quoteDetail.quoteHeaderId = data.quoteId;
              // quoteDetail.createdFromSourceType = 'SA';
              // quoteDetail.createdFromSourceReference = data.appointmentId;

              // campaignQuotesToCreate.push({
              //   quoteDetailForCampaign: quoteDetail,
              //   campaignDate: new Date().toISOString(),
              // });

              // const quoteDetail2: QuoteDetail = new QuoteDetail();
              // quoteDetail2.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
              // quoteDetail2.rooftopId = data.employee.rooftopId;
              // quoteDetail2.employeeId = data.employee.associateId;
              // quoteDetail2.associateId = data.employee.associateId;

              // quoteDetail2.serviceName =
              //   'SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted SOS, somehing on this thing is busted ';
              // quoteDetail2.campaignAddedBySystem = '1';
              // quoteDetail2.campaignNumber = 'N18912345';
              // quoteDetail2.technicianGroupId = null;
              // quoteDetail2.technicianGroupName = null;
              // quoteDetail2.technicianId = null;
              // quoteDetail2.labourOpCode = null;
              // quoteDetail2.recommendedHours = null;

              // quoteDetail2.warrantySubmissionTypeId = 'CC';

              // quoteDetail2.rateType = null;
              // quoteDetail2.rate = 0;
              // quoteDetail2.totalAmount = 0;
              // quoteDetail2.expirationDate = new Date();
              // quoteDetail2.expirationDate.setMonth(quoteDetail2.expirationDate.getMonth() + 1);

              // quoteDetail2.quoteHeaderId = data.quoteId;
              // quoteDetail2.createdFromSourceType = 'SA';
              // quoteDetail2.createdFromSourceReference = data.appointmentId;

              // campaignQuotesToCreate.push({
              //   quoteDetailForCampaign: quoteDetail2,
              //   campaignDate: new Date().toISOString(),
              // });
              // TESTING ONLY

              if (campaignQuotesToCreate.length > 0) {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: campaignQuotesToCreate,
                  loading: false,
                });
              } else {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: [],
                  loading: false,
                });
              }
            });
        }
      });
  }

  retrieveFordDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.oemDashboardData$.pipe(
        filter((fordDashboardData) => fordDashboardData != null),
        map((fordDashboardData: any) => {
          return fordDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, fordDashboardData, employee, appointmentId]) => {
          return { quoteId, fordDashboardData, employee, appointmentId };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('fordDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.fordDashboardData);

        if (data.fordDashboardData != null) {
          const dashboardCampaigns = data.fordDashboardData?.dashboardJson?.ServiceCampaign?.Campaign
            ? data.fordDashboardData.dashboardJson.ServiceCampaign.Campaign
            : [];

          if (Array.isArray(dashboardCampaigns) && dashboardCampaigns.length > 0) {
            const campaignQueryParams = new CampaignHeaderQueryParameters({
              id: dashboardCampaigns.map((campaign: any) => campaign.CampaignId).join(','),
            });
            this.apiService
              .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
              .pipe(take(1))
              .subscribe((campaigns) => {
                const missingCampaignHeaders = dashboardCampaigns.filter((c1: any) =>
                  campaigns.every((c2: CampaignHeader) => c2.id !== c1.CampaignId)
                );
                dashboardCampaigns.forEach((c1: any) => {
                  campaigns.forEach((c2: CampaignHeader) => {
                    if (c1.CampaignId === c2.id && c1.CampaignDesc !== c2.description && c2.description !== null) {
                      c1.CampaignDescription = c2.description;
                    }
                  });
                });
                if (missingCampaignHeaders) {
                  missingCampaignHeaders.forEach((campaign: any) => {
                    const campaignHeader: CampaignHeader = {
                      campaignJson: null,
                      chryslerFailureCode: null,
                      description: null,
                      estimatedTime: 0,
                      fordClaimTypesId: null,
                      fordConditionCodesId: null,
                      fordCustomerConcernCodesId: null,
                      id: campaign.CampaignId,
                      manufacturerId: 'FO',
                      name: campaign.CampaignDescription,
                      openFlag: '0',
                      operationCode: null,
                      recommendedHours: 0,
                      technicianGroupId: null,
                      technicianGroupName: null,
                      technicianId: null,
                      typeId: null,
                    };

                    this.apiService
                      .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                      .subscribe();
                  });
                }
              });
          }

          const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          params.quoteHeaderId = data.quoteId;
          this.apiService
            .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
            .pipe(take(1))
            .subscribe((quoteLines: QuoteDetail[]) => {
              const campaignQuotesToCreate: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[] = [];
              dashboardCampaigns.forEach((campaign: any) => {
                if (
                  quoteLines.findIndex(
                    (ql) =>
                      ql.campaignNumber == campaign.CampaignId &&
                      ql.createdFromSourceReference == data.appointmentId &&
                      ql.createdFromSourceType == 'SA'
                  ) == -1
                ) {
                  const quoteDetail: QuoteDetail = new QuoteDetail();
                  quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                  quoteDetail.rooftopId = data.employee.rooftopId;
                  quoteDetail.employeeId = data.employee.associateId;
                  quoteDetail.associateId = data.employee.associateId;

                  quoteDetail.serviceName = campaign.CampaignDescription;
                  quoteDetail.campaignAddedBySystem = '1';
                  quoteDetail.campaignNumber = campaign.CampaignId;

                  quoteDetail.warrantySubmissionTypeId = 'CC';

                  quoteDetail.rateType = null;
                  quoteDetail.rate = 0;
                  quoteDetail.totalAmount = 0;
                  //quoteDetail.quoteCreated = new Date();
                  quoteDetail.expirationDate = new Date();
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

                  quoteDetail.quoteHeaderId = data.quoteId;
                  quoteDetail.createdFromSourceType = 'SA';
                  quoteDetail.createdFromSourceReference = data.appointmentId;
                  if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                    campaignQuotesToCreate.push({
                      quoteDetailForCampaign: quoteDetail,
                      campaignDate: null,
                    });
                  }
                }
              });
              if (campaignQuotesToCreate.length > 0) {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: campaignQuotesToCreate,
                  loading: false,
                });
              } else {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: [],
                  loading: false,
                });
              }
            });
        }
      });
  }

  retrieveToyotaDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.oemDashboardData$.pipe(
        filter((toyotaDashboardData) => toyotaDashboardData != null),
        map((toyotaDashboardData: any) => {
          return toyotaDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, toyotaDashboardData, employee, appointmentId]) => {
          return { quoteId, toyotaDashboardData, employee, appointmentId };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('toyotaDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.toyotaDashboardData);

        if (Array.isArray(data?.toyotaDashboardData?.campaigns) && data?.toyotaDashboardData?.campaigns.length !== 0) {
          const dashboardCampaigns = data.toyotaDashboardData.campaigns;
          const campaignQueryParams = new CampaignHeaderQueryParameters({
            id: dashboardCampaigns.map((campaign: any) => campaign.CampaignNumber).join(','),
          });
          this.apiService
            .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
            .pipe(take(1))
            .subscribe((campaigns) => {
              const missingCampaignHeaders = dashboardCampaigns.filter((c1: any) =>
                campaigns.every((c2: CampaignHeader) => c2.id !== c1.CampaignNumber)
              );
              dashboardCampaigns.forEach((c1: any) => {
                campaigns.forEach((c2: CampaignHeader) => {
                  if (c1.CampaignNumber === c2.id && c1.CampaignDesc !== c2.description && c2.description !== null) {
                    c1.CampaignDesc = c2.description;
                  }
                });
              });
              if (missingCampaignHeaders) {
                missingCampaignHeaders.forEach((campaign: any) => {
                  const campaignHeader: CampaignHeader = {
                    campaignJson: null,
                    chryslerFailureCode: null,
                    description: null,
                    estimatedTime: 0,
                    fordClaimTypesId: null,
                    fordConditionCodesId: null,
                    fordCustomerConcernCodesId: null,
                    id: campaign.campaignNumber,
                    manufacturerId: 'TOY',
                    name: campaign.opCodeDescription,
                    openFlag: '0',
                    operationCode: campaign.opCode,
                    recommendedHours: campaign.allowanceHours,
                    technicianGroupId: null,
                    technicianGroupName: null,
                    technicianId: null,
                    typeId: campaign.CampaignType,
                  };

                  this.apiService
                    .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                    .subscribe();
                });
              }
            });

          const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          params.quoteHeaderId = data.quoteId;
          this.apiService
            .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
            .pipe(take(1))
            .subscribe((quoteLines: QuoteDetail[]) => {
              const campaignQuotesToCreate: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[] = [];

              dashboardCampaigns.forEach((campaign: any) => {
                if (
                  quoteLines.findIndex(
                    (ql) =>
                      ql.campaignNumber == campaign.campaignNumber &&
                      ql.createdFromSourceReference == data.appointmentId &&
                      ql.createdFromSourceType == 'SA'
                  ) == -1
                ) {
                  const quoteDetail: QuoteDetail = new QuoteDetail();
                  quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                  quoteDetail.rooftopId = data.employee.rooftopId;
                  quoteDetail.employeeId = data.employee.associateId;
                  quoteDetail.associateId = data.employee.associateId;

                  quoteDetail.serviceName = campaign.opCodeDescription;
                  quoteDetail.campaignAddedBySystem = '1';
                  quoteDetail.campaignNumber = campaign.campaignNumber;

                  quoteDetail.warrantySubmissionTypeId = 'CC';

                  quoteDetail.rateType = null;
                  quoteDetail.rate = 0;
                  quoteDetail.totalAmount = 0;
                  quoteDetail.expirationDate = new Date();
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

                  quoteDetail.quoteHeaderId = data.quoteId;
                  quoteDetail.createdFromSourceType = 'SA';
                  quoteDetail.createdFromSourceReference = data.appointmentId;
                  if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                    campaignQuotesToCreate.push({
                      quoteDetailForCampaign: quoteDetail,
                      campaignDate: null,
                    });
                  }
                }
              });
              if (campaignQuotesToCreate.length > 0) {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: campaignQuotesToCreate,
                  loading: false,
                });
              } else {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: [],
                  loading: false,
                });
              }
            });
        } else {
          this.serviceLaneFacade.campaignsToCreate$.next({
            campaignData: [],
            loading: false,
          });
        }
      });
  }

  retrieveChryslerDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.oemDashboardData$.pipe(
        filter((chryslerDashboardData) => chryslerDashboardData != null),
        map((chryslerDashboardData: any) => {
          return chryslerDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, chryslerDashboardData, employee, appointmentId]) => {
          return { quoteId, chryslerDashboardData, employee, appointmentId };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('chryslerDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.chryslerDashboardData);
        if (data?.chryslerDashboardData?.dashboard?.vip?.recallInfo?.recall != null) {
          let recalls: any[] = [];
          if (Array.isArray(data?.chryslerDashboardData?.dashboard?.vip?.recallInfo?.recall)) {
            recalls = data?.chryslerDashboardData?.dashboard?.vip?.recallInfo?.recall.filter((recall: any) => {
              return recall._attributes.reclStatus == 'Incomplete';
            });
          } else {
            if (
              data?.chryslerDashboardData?.dashboard?.vip?.recallInfo?.recall._attributes.reclStatus === 'Incomplete'
            ) {
              recalls = [data?.chryslerDashboardData?.dashboard?.vip?.recallInfo?.recall];
            }
          }
          if (recalls.length !== 0) {
            const campaignQueryParams = new CampaignHeaderQueryParameters({
              id: recalls.map((recall: any) => recall._attributes.reclNum).join(','),
            });
            this.apiService
              .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
              .pipe(take(1))
              .subscribe((campaigns) => {
                const missingRecalls = recalls.filter((recall: any) =>
                  campaigns.every((campaign: CampaignHeader) => campaign.id !== recall._attributes.reclNum)
                );
                recalls.forEach((recall: any) => {
                  campaigns.forEach((campaign: CampaignHeader) => {
                    if (
                      recall._attributes.reclNum === campaign.id &&
                      recall._attributes.reclDesc !== campaign.description &&
                      campaign.description !== null
                    ) {
                      recall._attributes.reclDesc = campaign.description;
                    }
                  });
                });
                if (missingRecalls) {
                  missingRecalls.forEach((recall: any) => {
                    const campaignHeader: CampaignHeader = {
                      campaignJson: null,
                      chryslerFailureCode: null,
                      description: null,
                      estimatedTime: 0,
                      fordClaimTypesId: null,
                      fordConditionCodesId: null,
                      fordCustomerConcernCodesId: null,
                      id: recall._attributes.reclNum,
                      manufacturerId: 'CH',
                      name: recall._attributes.reclDesc,
                      openFlag: '0',
                      operationCode: null,
                      recommendedHours: 0,
                      technicianGroupId: null,
                      technicianGroupName: null,
                      technicianId: null,
                      typeId: null,
                    };

                    this.apiService
                      .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                      .pipe(take(1))
                      .subscribe();
                  });
                }
              });

            const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
            params.quoteHeaderId = data.quoteId;
            this.apiService
              .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
              .pipe(take(1))
              .subscribe((quoteLines: QuoteDetail[]) => {
                const campaignQuotesToCreate: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[] = [];

                recalls.forEach((recall: any) => {
                  if (
                    quoteLines.findIndex(
                      (ql) =>
                        ql.campaignNumber == recall._attributes.reclNum &&
                        ql.createdFromSourceReference == data.appointmentId &&
                        ql.createdFromSourceType == 'SA'
                    ) == -1
                  ) {
                    const quoteDetail: QuoteDetail = new QuoteDetail();
                    quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                    quoteDetail.rooftopId = data.employee.rooftopId;
                    quoteDetail.employeeId = data.employee.associateId;
                    quoteDetail.associateId = data.employee.associateId;
                    quoteDetail.serviceName = recall._attributes.reclDesc;
                    quoteDetail.campaignAddedBySystem = '1';
                    quoteDetail.campaignNumber = recall._attributes.reclNum;
                    quoteDetail.warrantySubmissionTypeId = 'CC';
                    quoteDetail.rateType = null;
                    quoteDetail.rate = 0;
                    quoteDetail.totalAmount = 0;
                    quoteDetail.expirationDate = new Date();
                    quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
                    quoteDetail.quoteHeaderId = data.quoteId;
                    quoteDetail.createdFromSourceType = 'SA';
                    quoteDetail.createdFromSourceReference = data.appointmentId;

                    if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                      const recallDate = recalls.find(
                        (recall: any) => recall._attributes.reclNum === quoteDetail.campaignNumber
                      ).reclLaunchDt.date._attributes;

                      campaignQuotesToCreate.push({
                        quoteDetailForCampaign: quoteDetail,
                        campaignDate: new Date(recallDate.year, recallDate.month, recallDate.day).toISOString(),
                      });
                    }
                  }
                });
                if (campaignQuotesToCreate.length > 0) {
                  this.serviceLaneFacade.campaignsToCreate$.next({
                    campaignData: campaignQuotesToCreate,
                    loading: false,
                  });
                } else {
                  this.serviceLaneFacade.campaignsToCreate$.next({
                    campaignData: [],
                    loading: false,
                  });
                }
              });
          } else {
            this.serviceLaneFacade.campaignsToCreate$.next({
              campaignData: [],
              loading: false,
            });
          }
        }
      });
  }

  retrieveNissanDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.oemDashboardData$.pipe(
        filter((nissanDashboardData) => nissanDashboardData != null),
        map((nissanDashboardData: any) => {
          return nissanDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, nissanDashboardData, employee, appointmentId]) => {
          return { quoteId, nissanDashboardData, employee, appointmentId };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('nissanDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.nissanDashboardData);
        if (
          data?.nissanDashboardData?.dashboard?.ShowVehicleServiceHistory?.ShowVehicleServiceHistoryDataArea
            ?.VehicleServiceHistory?.VehicleServiceHistoryHeader?.ServiceCampaign != null
        ) {
          let recalls: any[] = [];
          if (
            Array.isArray(
              data?.nissanDashboardData?.dashboard?.ShowVehicleServiceHistory?.ShowVehicleServiceHistoryDataArea
                ?.VehicleServiceHistory?.VehicleServiceHistoryHeader?.ServiceCampaign
            )
          ) {
            recalls =
              data?.nissanDashboardData?.dashboard?.ShowVehicleServiceHistory?.ShowVehicleServiceHistoryDataArea
                ?.VehicleServiceHistory?.VehicleServiceHistoryHeader?.ServiceCampaign;
          } else {
            recalls = [
              data?.nissanDashboardData?.dashboard?.ShowVehicleServiceHistory?.ShowVehicleServiceHistoryDataArea
                ?.VehicleServiceHistory?.VehicleServiceHistoryHeader?.ServiceCampaign,
            ];
          }

          const campaignQueryParams = new CampaignHeaderQueryParameters({
            id: recalls.map((recall: any) => recall.CampaignNumberString._text).join(','),
          });
          this.apiService
            .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
            .pipe(take(1))
            .subscribe((campaigns) => {
              const missingRecalls = recalls.filter((recall: any) =>
                campaigns.every((campaign: CampaignHeader) => campaign.id !== recall.CampaignNumberString._text)
              );
              recalls.forEach((recall: any) => {
                campaigns.forEach((campaign: CampaignHeader) => {
                  if (
                    recall.CampaignNumberString._text === campaign.id &&
                    recall.CampaignDescription._text !== campaign.description &&
                    campaign.description !== null
                  ) {
                    recall.CampaignDescription._text = campaign.description;
                  }
                });
              });
              if (missingRecalls) {
                missingRecalls.forEach((recall: any) => {
                  const campaignHeader: CampaignHeader = {
                    campaignJson: null,
                    chryslerFailureCode: null,
                    description: null,
                    estimatedTime: 0,
                    fordClaimTypesId: null,
                    fordConditionCodesId: null,
                    fordCustomerConcernCodesId: null,
                    id: recall._attributes.reclNum,
                    manufacturerId: 'CH',
                    name: recall.CampaignDescription._text,
                    openFlag: '0',
                    operationCode: null,
                    recommendedHours: 0,
                    technicianGroupId: null,
                    technicianGroupName: null,
                    technicianId: null,
                    typeId: null,
                  };

                  this.apiService
                    .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                    .pipe(take(1))
                    .subscribe();
                });
              }
            });

          const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          params.quoteHeaderId = data.quoteId;
          this.apiService
            .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
            .pipe(take(1))
            .subscribe((quoteLines: QuoteDetail[]) => {
              const campaignQuotesToCreate: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[] = [];
              recalls.forEach((recall: any) => {
                if (
                  quoteLines.findIndex(
                    (ql) =>
                      ql.campaignNumber == recall.CampaignNumberString._text &&
                      ql.createdFromSourceReference == data.appointmentId &&
                      ql.createdFromSourceType == 'SA'
                  ) == -1
                ) {
                  const quoteDetail: QuoteDetail = new QuoteDetail();
                  quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                  quoteDetail.rooftopId = data.employee.rooftopId;
                  quoteDetail.employeeId = data.employee.associateId;
                  quoteDetail.associateId = data.employee.associateId;
                  quoteDetail.serviceName = recall.CampaignDescription._text;
                  quoteDetail.campaignAddedBySystem = '1';
                  quoteDetail.campaignNumber = recall.CampaignNumberString._text;
                  quoteDetail.warrantySubmissionTypeId = 'CC';
                  quoteDetail.rateType = null;
                  quoteDetail.rate = 0;
                  quoteDetail.totalAmount = 0;
                  quoteDetail.expirationDate = new Date();
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
                  quoteDetail.quoteHeaderId = data.quoteId;
                  quoteDetail.createdFromSourceType = 'SA';
                  quoteDetail.createdFromSourceReference = data.appointmentId;
                  if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                    campaignQuotesToCreate.push({
                      quoteDetailForCampaign: quoteDetail,
                      campaignDate: null,
                    });
                  }
                }
              });

              if (campaignQuotesToCreate.length > 0) {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: campaignQuotesToCreate,
                  loading: false,
                });
              } else {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: [],
                  loading: false,
                });
              }
            });
        } else {
          this.serviceLaneFacade.campaignsToCreate$.next({
            campaignData: [],
            loading: false,
          });
        }
      });
  }

  retrieveHyundaiDashboardDataAndApplyRecalls() {
    this.dashboardSubscription$ = combineLatest([
      this.inspectionService.quoteId$.pipe(
        filter((quoteId) => quoteId != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.hyundaiDashboardData$.pipe(
        filter((hyundaiDashboardData) => hyundaiDashboardData != null),
        map((hyundaiDashboardData: any) => {
          return hyundaiDashboardData;
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => {
          return appointment ? appointment.id : null;
        }),
        distinctUntilChanged()
      ),
      this.serviceLaneFacade.vehicle$.pipe(
        filter((vehicle) => vehicle != null),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        map(([quoteId, hyundaiDashboardData, employee, appointmentId, vehicle]) => {
          return { quoteId, hyundaiDashboardData, employee, appointmentId, vehicle };
        }),
        filter((data) => data.appointmentId != null),
        distinctUntilKeyChanged('appointmentId'),
        distinctUntilKeyChanged('hyundaiDashboardData')
      )
      .subscribe((data: any) => {
        this.serviceLaneFacade.dashboardData$.next(data.hyundaiDashboardData);

        if (data?.hyundaiDashboardData?.dashboard?.vehicleData) {
          const updatedVehicle: Vehicle = new DeepCopyPipe().transform(data.vehicle);
          updatedVehicle.warrantyEffectiveDate = data?.hyundaiDashboardData?.dashboard?.vehicleData.warrantyStartDate;
          updatedVehicle.interiorColourDescription = data?.hyundaiDashboardData?.dashboard?.vehicleData.InteriorColour;
          updatedVehicle.interiorColourCode = data?.hyundaiDashboardData?.dashboard?.vehicleData.InteriorColourCode;
          updatedVehicle.exteriorColour = data?.hyundaiDashboardData?.dashboard?.vehicleData.ExteriorColour;
          updatedVehicle.exteriorColourCode = data?.hyundaiDashboardData?.dashboard?.vehicleData.ExteriorColourCode;
          this.serviceLaneFacade.updateVehicleOnServer(updatedVehicle);
        }
        if (
          data?.hyundaiDashboardData?.dashboard?.recallCampaigns ||
          data?.hyundaiDashboardData?.dashboard?.serviceCampaigns
        ) {
          let recalls: any[] = [];
          if (data?.hyundaiDashboardData?.dashboard?.recallCampaigns) {
            if (Array.isArray(data?.hyundaiDashboardData?.dashboard?.recallCampaigns)) {
              recalls = recalls.concat(data?.hyundaiDashboardData?.dashboard?.recallCampaigns);
            } else {
              recalls.push(data?.hyundaiDashboardData?.dashboard?.recallCampaigns);
            }
          }

          if (data?.hyundaiDashboardData?.dashboard?.serviceCampaigns) {
            if (Array.isArray(data?.hyundaiDashboardData?.dashboard?.serviceCampaigns)) {
              recalls = recalls.concat(data?.hyundaiDashboardData?.dashboard?.serviceCampaigns);
            } else {
              recalls.push(data?.hyundaiDashboardData?.dashboard?.serviceCampaigns);
            }
          }
          recalls = recalls.filter((recall) => !recall.RepairStatus);
          const campaignQueryParams = new CampaignHeaderQueryParameters({
            id: recalls.map((recall: any) => recall.CampaignId).join(','),
          });
          this.apiService
            .get<CampaignHeader[]>('v/1/fixed-operations/campaign-headers', { params: campaignQueryParams })
            .pipe(take(1))
            .subscribe((campaigns: CampaignHeader[]) => {
              const missingRecalls = recalls.filter((recall: any) =>
                campaigns.every((campaign: CampaignHeader) => campaign.id !== recall.CampaignId)
              );
              recalls.forEach((recall: any) => {
                campaigns.forEach((campaign: CampaignHeader) => {
                  if (
                    recall.CampaignId === campaign.id &&
                    recall.HACCDescription !== campaign.description &&
                    campaign.description !== null
                  ) {
                    recall.HACCDescription = campaign.description;
                  }
                });
              });
              if (missingRecalls) {
                missingRecalls.forEach((recall: any) => {
                  const campaignHeader: CampaignHeader = {
                    campaignJson: null,
                    chryslerFailureCode: null,
                    description: recall.HACCDescription,
                    estimatedTime: 0,
                    fordClaimTypesId: null,
                    fordConditionCodesId: null,
                    fordCustomerConcernCodesId: null,
                    id: recall.CampaignId,
                    manufacturerId: 'HYU',
                    name: recall.HACCName,
                    openFlag: '0',
                    operationCode: null,
                    recommendedHours: 0,
                    technicianGroupId: null,
                    technicianGroupName: null,
                    technicianId: null,
                    typeId: null,
                  };

                  this.apiService
                    .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', campaignHeader)
                    .pipe(take(1))
                    .subscribe();
                });
              }
            });

          const params: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          params.quoteHeaderId = data.quoteId;
          this.apiService
            .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, { params })
            .pipe(take(1))
            .subscribe((quoteLines: QuoteDetail[]) => {
              const campaignQuotesToCreate: {
                quoteDetailForCampaign: QuoteDetail;
                campaignDate: string;
              }[] = [];
              recalls.forEach((recall: any) => {
                if (
                  quoteLines.findIndex(
                    (ql) =>
                      ql.campaignNumber == recall.CampaignId &&
                      ql.createdFromSourceReference == data.appointmentId &&
                      ql.createdFromSourceType == 'SA'
                  ) == -1
                ) {
                  const quoteDetail: QuoteDetail = new QuoteDetail();
                  quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
                  quoteDetail.rooftopId = data.employee.rooftopId;
                  quoteDetail.employeeId = data.employee.associateId;
                  quoteDetail.associateId = data.employee.associateId;
                  quoteDetail.serviceName = recall.HACCName;
                  quoteDetail.campaignAddedBySystem = '1';
                  quoteDetail.campaignNumber = recall.CampaignId;
                  quoteDetail.warrantySubmissionTypeId = 'WC';
                  quoteDetail.rateType = null;
                  quoteDetail.rate = 0;
                  quoteDetail.totalAmount = 0;
                  quoteDetail.expirationDate = new Date();
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
                  quoteDetail.quoteHeaderId = data.quoteId;
                  quoteDetail.createdFromSourceType = 'SA';
                  quoteDetail.createdFromSourceReference = data.appointmentId;
                  if (!this.campaignNumbers.includes(quoteDetail.campaignNumber)) {
                    campaignQuotesToCreate.push({
                      quoteDetailForCampaign: quoteDetail,
                      campaignDate: recalls.find((recall: any) => recall.CampaignId === quoteDetail.campaignNumber)
                        .StartDate,
                    });
                  }
                }
              });
              if (campaignQuotesToCreate.length > 0) {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: campaignQuotesToCreate,
                  loading: false,
                });
              } else {
                this.serviceLaneFacade.campaignsToCreate$.next({
                  campaignData: [],
                  loading: false,
                });
              }
            });

          if (data?.hyundaiDashboardData?.dashboard?.recallCampaignParts) {
            const recallCampaignPartsArr = data.hyundaiDashboardData.dashboard.recallCampaignParts;

            recallCampaignPartsArr.map((campaignPartsObj: any) => {
              /* handle recall campaigns only with 1 campaign operation */
              if (
                Array.isArray(campaignPartsObj.recallCampaignParts) &&
                campaignPartsObj.recallCampaignParts.length === 1
              ) {
                this.apiService
                  .get<CampaignHeader[]>(`v/1/fixed-operations/campaign-headers/${campaignPartsObj.recallCampaignId}`)
                  .subscribe((campaignHeader) => {
                    /* if campaign header already exists and its opCode,estTime, or recHrs are different - update its Rec.Hrs,EstTime,LabourOp */
                    if (campaignHeader) {
                      if (
                        campaignHeader.estimatedTime !== campaignPartsObj.recallCampaignParts[0].LabourTime ||
                        campaignHeader.operationCode !== campaignPartsObj.recallCampaignParts[0].CampaignOperationId ||
                        campaignHeader.recommendedHours !== campaignPartsObj.recallCampaignParts[0].LabourTime
                      ) {
                        const updatedCampaignHeader: CampaignHeader = {
                          ...campaignHeader,
                          estimatedTime: campaignPartsObj.recallCampaignParts[0].LabourTime,
                          operationCode: campaignPartsObj.recallCampaignParts[0].CampaignOperationId,
                          recommendedHours: campaignPartsObj.recallCampaignParts[0].LabourTime,
                        };
                        this.apiService
                          .put<CampaignHeader[]>(
                            `v/1/fixed-operations/campaign-headers/${campaignPartsObj.recallCampaignId}`,
                            updatedCampaignHeader
                          )
                          .subscribe((data) => {});
                      }
                    } else {
                      /* if campaign header doesn't exist - create one including id(recallCampaignId),Rec.Hrs,EstTime,LabourOp */
                      const matchingRecall = data?.hyundaiDashboardData?.dashboard?.recallCampaigns.find(
                        (recall: any) => recall.CampaignId === campaignPartsObj.recallCampaignId
                      );

                      const newCampaignHeader: CampaignHeader = {
                        campaignJson: null,
                        chryslerFailureCode: null,
                        description: matchingRecall ? matchingRecall.HACCDescription : null,
                        estimatedTime: campaignPartsObj.recallCampaignParts[0].LabourTime,
                        fordClaimTypesId: null,
                        fordConditionCodesId: null,
                        fordCustomerConcernCodesId: null,
                        id: campaignPartsObj.recallCampaignId,
                        manufacturerId: 'HYU',
                        name: matchingRecall ? matchingRecall.HACCName : null,
                        openFlag: '0',
                        operationCode: campaignPartsObj.recallCampaignParts[0].CampaignOperationId,
                        recommendedHours: campaignPartsObj.recallCampaignParts[0].LabourTime,
                        technicianGroupId: null,
                        technicianGroupName: null,
                        technicianId: null,
                        typeId: null,
                      };

                      this.apiService
                        .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', newCampaignHeader)
                        .pipe(take(1))
                        .subscribe();
                    }
                  });
              }

              /* handle recall campaigns with No parts found */
              if (
                !Array.isArray(campaignPartsObj.recallCampaignParts) &&
                campaignPartsObj.recallCampaignParts == 'No parts found'
              ) {
                this.apiService
                  .get<CampaignHeader[]>(`v/1/fixed-operations/campaign-headers/${campaignPartsObj.recallCampaignId}`)
                  .subscribe((campaignHeader) => {
                    /* if campaign header doesn't exist - create one including id(recallCampaignId), name, desc; no data on Rec.Hrs,EstTime,LabourOp */
                    if (!campaignHeader) {
                      const matchingRecall = data?.hyundaiDashboardData?.dashboard?.recallCampaigns.find(
                        (recall: any) => recall.CampaignId === campaignPartsObj.recallCampaignId
                      );

                      const newCampaignHeader: CampaignHeader = {
                        campaignJson: null,
                        chryslerFailureCode: null,
                        description: matchingRecall ? matchingRecall.HACCDescription : null,
                        estimatedTime: 0,
                        fordClaimTypesId: null,
                        fordConditionCodesId: null,
                        fordCustomerConcernCodesId: null,
                        id: campaignPartsObj.recallCampaignId,
                        manufacturerId: 'HYU',
                        name: matchingRecall ? matchingRecall.HACCName : null,
                        openFlag: '0',
                        operationCode: null,
                        recommendedHours: 0,
                        technicianGroupId: null,
                        technicianGroupName: null,
                        technicianId: null,
                        typeId: null,
                      };

                      this.apiService
                        .post<CampaignHeader>('v/1/fixed-operations/campaign-headers', newCampaignHeader)
                        .pipe(take(1))
                        .subscribe();
                    }
                  });
              }
            });
          }
        } else {
          this.serviceLaneFacade.campaignsToCreate$.next({
            campaignData: [],
            loading: false,
          });
        }
      });
  }

  getOemDescriptionFromCode(oem: string) {
    switch (oem) {
      case 'GM':
        return 'GM';
      case 'FO':
        return 'Ford';
      case 'TOY':
        return 'Toyota';
      case 'LX':
        return 'Lexus';
      case 'CH':
        return 'Chrysler';
      case 'KIA':
        return 'Kia';
      case 'HYU':
        return 'Hyundai';
      default:
        return '';
    }
  }

  visitStuff() {
    this.visitSub$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedUser().pipe(filter((au) => au != null)),
      this.serviceLaneFacade.vehicle$.pipe(
        filter((vehicle) => vehicle != null),
        distinctUntilKeyChanged('id')
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee != null),
        switchMap((employee) => {
          return this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
            map((oemRooftopLink) => {
              if (oemRooftopLink) {
                this.oem = oemRooftopLink.associateId;
                return oemRooftopLink;
              }
              return null;
            })
          );
        })
      ),
      this.vehicleDiagnostics$,
    ]).subscribe(([authenticatedUser, vehicle, oemRooftopLink, vehicleDiagnostics]) => {
      this.appointmentId = this.route.snapshot.params.appointmentId;
      this.vehicleId = this.route.snapshot.params.vehicleId;

      switch (oemRooftopLink.associateId) {
        case 'GM':
          if (vehicleDiagnostics.enabled) {
            combineLatest([
              this.oemMenuService.businessId$.pipe(
                filter((businessId) => businessId != null),
                take(1)
              ),
              this.authenticationStateService.selectAuthenticatedUser().pipe(
                filter((authenticatedUser) => authenticatedUser != null),
                tap((authenticatedUser) => {
                  this.oemMenuService.initialize(authenticatedUser.user.destination);
                })
              ),
              this.svcVehicleDiagnosticsRootFacade.dealerBac$,
            ])
              .pipe(takeUntil(this.destroy$))
              .subscribe(([businessId, authenticatedUser, dealerBac]) => {
                const oneYearAgo = new Date();
                oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                oneYearAgo.setHours(0, 0, 0);

                const today = new Date();
                today.setHours(23, 59, 59);

                if (this.serviceLaneFacade.isLeapYear(today)) {
                  oneYearAgo.setDate(oneYearAgo.getDate() + 1);
                }

                this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByVin(
                  businessId,
                  vehicleDiagnostics.dealerCountryCode,
                  vehicle.vin,
                  new GMServiceVisitByVinQueryParameters({
                    startDate: oneYearAgo.toISOString(),
                    endDate: today.toISOString(),
                    bac: dealerBac,
                    destinationId: authenticatedUser.user.destination,
                  })
                );

                this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByDealer(
                  businessId,
                  vehicleDiagnostics.dealerCountryCode,
                  oneYearAgo.toISOString(),
                  today.toISOString(),
                  dealerBac,
                  new GMServiceVisitByDealerQueryParameters({ destinationId: authenticatedUser.user.destination })
                );

                this.svcVehicleDiagnosticsRootFacade
                  .gmActionListener(
                    businessId,
                    vehicleDiagnostics.dealerCountryCode,
                    vehicle.vin,
                    this.appointmentId,
                    'appointment',
                    false
                  )
                  .pipe(takeUntil(this.destroy$))
                  .subscribe();

                combineLatest([
                  this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$.pipe(
                    filter((vinStatus: status) => vinStatus === status.succeeded)
                  ),
                  this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVin$.pipe(
                    filter((serviceVisit) => serviceVisit.length > 0)
                  ),
                  this.serviceLaneFacade.selectedAppointment$.pipe(filter((appointment) => appointment != null)),
                  this.svcVehicleDiagnosticsRootFacade.getAlreadyLinkedServiceVisitIds$,
                ])
                  .pipe(
                    take(1),
                    tap(
                      ([
                        serviceVistByVinStateStatus,
                        serviceVisitsByVin,
                        selectedAppointment,
                        alreadyLinkedServiceVisitIds,
                      ]) => {
                        if (selectedAppointment.serviceVisitId === null) {
                          const mostRecentServiceVisit: GMServiceVisitByVin | GMServiceVisitByDealer =
                            this.svcVehicleDiagnosticsRootFacade.getLatestServiceVisit(serviceVisitsByVin);
                          // only automatically link if the most recent visit is within last 24 hours and not already linked
                          if (
                            this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                              mostRecentServiceVisit.checkInDate,
                              mostRecentServiceVisit.checkInTime
                            ) &&
                            !alreadyLinkedServiceVisitIds.includes(mostRecentServiceVisit.serviceVisitId)
                          ) {
                            this.svcVehicleDiagnosticsRootFacade.isWithinADay$.next(
                              this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                                mostRecentServiceVisit.checkInDate,
                                mostRecentServiceVisit.checkInTime
                              )
                            );
                            this.updateLinkedServiceVisit(mostRecentServiceVisit.serviceVisitId);
                          }
                        } else {
                          const currentlyLinkedServiceVisit: GMServiceVisitByVin | GMServiceVisitByDealer =
                            serviceVisitsByVin.find(
                              (serviceVisit) => serviceVisit.serviceVisitId === selectedAppointment.serviceVisitId
                            );
                          this.svcVehicleDiagnosticsRootFacade.isWithinADay$.next(
                            this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                              currentlyLinkedServiceVisit.checkInDate,
                              currentlyLinkedServiceVisit.checkInTime
                            )
                          );

                          this.updateLinkedServiceVisit(selectedAppointment.serviceVisitId);
                        }
                      }
                    )
                  )
                  .subscribe();
              });
          }
          this.retrieveGMDashboardDataAndApplyRecalls();
          this.listenForServicesFromMenu();
          break;
        case 'FO':
          this.retrieveFordDashboardDataAndApplyRecalls();
          this.listenForServicesFromMenu();
          break;
        case 'TOY':
          this.retrieveToyotaDashboardDataAndApplyRecalls();
          this.listenForServicesFromToyotaMenu();
          break;
        case 'CH':
          this.retrieveChryslerDashboardDataAndApplyRecalls();
          this.listenForServicesFromMenu();
          break;
        case 'NIS':
          this.retrieveNissanDashboardDataAndApplyRecalls();
          this.listenForServicesFromMenu();
          break;
        case 'HYU':
          this.authenticationStateService
            .selectAuthenticatedEmployee()
            .pipe(
              filter((emp) => emp != null),
              take(1),
              switchMap((employee) => {
                return this.systemControlStateService
                  .getSystemControlValues(['CL_HYUNDAI_INTEGRATION_ENABLED'], employee.rooftopId)
                  .pipe(
                    take(1),
                    map((scts) => {
                      return scts['CL_HYUNDAI_INTEGRATION_ENABLED'].value;
                    })
                  );
              })
            )
            .subscribe((sct) => {
              if (sct == '1') {
                this.retrieveHyundaiDashboardDataAndApplyRecalls();
                this.listenForServicesFromHyundaiMenu();
              } else {
                this.listenForServicesFromMenu();
              }
            });

          break;
        default:
          this.listenForServicesFromMenu();
          break;
      }

      this.dashboardRetrievalSubscription$ = combineLatest([
        this.serviceLaneFacade.selectedAppointment$.pipe(filter((appointment) => appointment != null)),
        this.serviceLaneFacade.vehicle$.pipe(
          filter((vehicle) => vehicle != null),
          distinctUntilKeyChanged('id')
        ),
        this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
        this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee != null),
          take(1),
          switchMap((employee) => {
            return this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
              map((oemRooftopLink) => {
                return oemRooftopLink || null;
              })
            );
          })
        ),
      ])
        .pipe(
          map(([appointment, vehicle, employee, oem]) => {
            return { appointment, vehicle, employee, oem };
          }),
          distinctUntilKeyChanged('vehicle')
          //filter(data => data.params.appointmentId != null)
        )
        .subscribe((data) => {
          this.serviceLaneFacade.dashboardSubject$.next({
            params: {
              oemrtId: data.vehicle.rooftopId,
              vehicleVin: data.vehicle.vin,
              odometerIn: data.appointment.odometerIn
                ? data.appointment.odometerIn
                : data.vehicle.currentMileage
                ? data.vehicle.currentMileage
                : null,
              appointmentId: data.appointment.id,
              vehicleId: data.vehicle.id,
              employeeId: data.employee.associateId,
              oem: data.oem != null ? data.oem.associateId : null,
              vehicleYear: vehicle.year,
              vehicleManufacturer: data.vehicle.manufacturerId,
            },
            skipCache: false,
          });
        });

      this.routerStateSubscription$ = this.routerStateService
        .selectRouterState()
        .pipe(
          filter((routerState: any) => {
            return routerState.state.url.indexOf(`appointments`) > -1;
          })
        )
        .subscribe((routerState: any) => {
          if (routerState.state.root.firstChild.firstChild.firstChild.url[0]) {
            this.primaryRouteUrl = routerState.state.root.firstChild.firstChild.firstChild.url[0].path;
          }
        });

      const workOrderQueryParameters = new WorkOrderQueryParameters({
        appointmentId: this.route.snapshot.params.appointmentId.toString(),
      });

      this.workOrderSub$ = this.apiService
        .get(`v/1/fixed-operations/work-orders`, { params: workOrderQueryParameters })
        .pipe(
          map((workOrders: WorkOrder[]) => {
            if (workOrders.length > 0) {
              // appt to WO should be 1:1 but something created multiple WO, grab the first to prevent creating more
              return workOrders[0].id;
            } else {
              return null;
            }
          })
        )
        .subscribe((workOrderId) => {
          if (workOrderId != null) {
            this.inspectionService.triggerInspectionLogic.next(workOrderId);
            this.inspectionService.getQuote(workOrderId).then((quote) => {
              this.inspectionService.workOrderId$.next(workOrderId);
              // WOId should be leveraged within PL instead of inspection service
              this.serviceLaneFacade.workOrderId$.next(workOrderId);

              this.inspectionService.quoteId$.next(quote.QuoteId);
              this.inspectionService.workOrderId = workOrderId;
              this.inspectionService.quoteId = quote.QuoteId;
              this.inspectionService.vehicleId = this.route.snapshot.params.vehicleId;
              this.inspectionService.appointmentId = this.route.snapshot.params.appointmentId;

              this.mediaStateService.setMediaModelKey({
                vehicleId: this.inspectionService.vehicleId,
                appointmentId: this.inspectionService.appointmentId,
                workOrderId: this.inspectionService.workOrderId,
                quoteId: this.inspectionService.quoteId,
                destinationId: authenticatedUser.user.destination,
                customerFacing: true,
                key: 'appointmentCase',
              });
              this.inspectionService.inspectionSavedNotification.subscribe(() => {
                this.serviceLaneFacade.getQuoteLinesFromServer({
                  quoteHeaderId: quote.QuoteId.toString(),
                  embed: 'employeeAssociate,serviceGroup',
                });
              });
              this.vehicleMaintenanceService.scheduleSavedNotification.subscribe(() => {
                this.serviceLaneFacade.getQuoteLinesFromServer({
                  quoteHeaderId: quote.QuoteId.toString(),
                  embed: 'employeeAssociate,serviceGroup',
                });
              });
            });
          } else {
            this.authenticatedEmployeeSubscription$ = this.authenticationStateService
              .selectAuthenticatedEmployee()
              .pipe(filter((employee) => employee != null))
              .subscribe((employee) => {
                this.createReservedWorkOrderSubscription$ = combineLatest([
                  this.apiService.get(`v/1/vehicles/vehicles/${this.route.snapshot.params.vehicleId}`, {
                    params: { embed: 'type' },
                  }),
                  this.apiService.get(`v/2/fixed-operations/appointments/${this.route.snapshot.params.appointmentId}`),
                  this.systemControlStateService.getSystemControlValues(
                    [
                      //'CL_GM_DASHBOARD_INTEGRATION_ENABLED', --- Currently breaks any non GM rooftop.
                      'CL_ENABLE_MAINTENANCE_MENU',
                      'CL_COMMUNICATOR_ENABLE',
                      'CL_CURRENCY',
                      'CL_SHOP_SUPPLIES_PERCENT',
                      'CL_UNIT_OF_MEASURE',
                      'CL_ELECTRONIC_WORK_ORDER_FLAG',
                      'CL_CITY',
                      'CL_WORK_RECON_LINK_REF',
                      'CL_GLOBAL_WARRANTY',
                      'CL_WO_AUTO_TAG',
                    ],
                    employee.rooftopId
                  ),
                  this.serviceLaneFacade.combinedTax$,
                ])
                  .pipe(
                    map(([vehicle, appointment, systemControls, combinedTax]) => {
                      return { vehicle, appointment, systemControls, combinedTax };
                    }),
                    take(1)
                  )
                  .subscribe((data) => {
                    if (data.vehicle) {
                      this.createReservedWorkOrder(
                        data.appointment,
                        data.vehicle,
                        data.systemControls,
                        authenticatedUser.user.destination,
                        data.combinedTax
                      );
                    }
                  });
              });
          }
        });
    });
  }

  listenForServicesFromMenu() {
    this.servicesFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.acceptQuoteFromMenu$,
      this.inspectionService.quoteId$,
      this.inspectionService.workOrderId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, serviceGroup, quoteId, workOrderId, appointmentId]) => {
          return { employee, serviceGroup, quoteId, workOrderId, appointmentId };
        }),
        distinctUntilKeyChanged('serviceGroup'),
        //distinctUntilKeyChanged('appointmentId'),
        switchMap((data) => {
          let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          quoteDetailQueryParams.quoteHeaderId = data.quoteId.toString();
          quoteDetailQueryParams.serviceGroupId = data.serviceGroup.id;

          return this.serviceLaneFacade.quoteLineWithServiceGroupExists(data.serviceGroup.id, data.appointmentId).pipe(
            map((quoteExists) => {
              return {
                employee: data.employee,
                serviceGroup: data.serviceGroup,
                quoteExists,
                quoteId: data.quoteId,
                workOrderId: data.workOrderId,
                appointmentId: data.appointmentId,
              };
            }),
            take(1),
            distinctUntilKeyChanged('serviceGroup')
          );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            let quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            //quoteDetail.quoteHeaderId = this.quote.id;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.opCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.embedded.rate.recommendedHours != null
                ? data.serviceGroup.embedded.rate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rateType
                ? data.serviceGroup.embedded.rate.rateType
                : '1'
              : '1';
            quoteDetail.rate = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;

            quoteDetail.shopSupplyFlag = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.shopFlag
                ? data.serviceGroup.embedded.rate.shopFlag
                : null
              : null;
            quoteDetail.totalAmount = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.workHeaderId = data.workOrderId;

            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            this.serviceLaneFacade.approveQuoteLine(quoteDetail);
            //this.fixedOperationsService.createQuoteCase(quoteDetail);
          }
        }),
        share()
      )
      .subscribe((data) => {});

    this.declinedFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.vehicleMaintenanceService.declinedServicesWithReason,
      this.inspectionService.quoteId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, serviceGroupData, quoteId, appointmentId]) => {
          return {
            employee,
            serviceGroup: serviceGroupData.serviceGroup,
            declinedReason: serviceGroupData.declinedReason,
            quoteId,
            appointmentId,
          };
        }),
        distinctUntilKeyChanged('serviceGroup'),
        switchMap((data) => {
          let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          quoteDetailQueryParams.quoteHeaderId = data.quoteId.toString();
          quoteDetailQueryParams.serviceGroupId = data.serviceGroup.id;
          return this.serviceLaneFacade.quoteLineWithServiceGroupExists(data.serviceGroup.id, data.appointmentId).pipe(
            map((quoteExists) => {
              return {
                employee: data.employee,
                serviceGroup: data.serviceGroup,
                quoteExists,
                declinedReason: data.declinedReason,
                quoteId: data.quoteId,
                appointmentId: data.appointmentId,
              };
            }),
            take(1),
            distinctUntilKeyChanged('serviceGroup')
          );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            let quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            //quoteDetail.quoteHeaderId = this.quote.id;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.opCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.embedded.rate.recommendedHours != null
                ? data.serviceGroup.embedded.rate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rateType
                ? data.serviceGroup.embedded.rate.rateType
                : '1'
              : '1';
            quoteDetail.rate = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;
            quoteDetail.totalAmount = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            quoteDetail.declined = data.declinedReason.id;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            const declinedQuoteLine: QuoteDetail = new DeepCopyPipe().transform(quoteDetail);
            this.serviceLaneFacade.declineQuoteLine(declinedQuoteLine);
            //this.serviceLaneFacade.approveQuoteLine(quoteDetail);
            //this.fixedOperationsService.createQuoteCase(quoteDetail);
          }
        }),
        share()
      )
      .subscribe((data) => {});

    this.createQuoteLineOnServer$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.addQuoteLineFromMenu$,
      this.inspectionService.quoteId$,
      this.inspectionService.workOrderId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, serviceGroup, quoteId, workOrderId, appointmentId]) => {
          return { employee, serviceGroup, quoteId, workOrderId, appointmentId };
        }),
        distinctUntilKeyChanged('serviceGroup'),
        //distinctUntilKeyChanged('appointmentId'),
        switchMap((data) => {
          let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          quoteDetailQueryParams.quoteHeaderId = data.quoteId.toString();
          quoteDetailQueryParams.serviceGroupId = data.serviceGroup.id;

          return this.serviceLaneFacade.quoteLineWithServiceGroupExists(data.serviceGroup.id, data.appointmentId).pipe(
            map((quoteExists) => {
              return {
                employee: data.employee,
                serviceGroup: data.serviceGroup,
                quoteExists,
                quoteId: data.quoteId,
                workOrderId: data.workOrderId,
                appointmentId: data.appointmentId,
              };
            }),
            take(1),
            distinctUntilKeyChanged('serviceGroup')
          );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            let quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            //quoteDetail.quoteHeaderId = this.quote.id;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.opCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.labourOpCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.embedded.rate.recommendedHours != null
                ? data.serviceGroup.embedded.rate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rateType
                ? data.serviceGroup.embedded.rate.rateType
                : null
              : null;
            quoteDetail.rate = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;

            quoteDetail.shopSupplyFlag = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.shopFlag
                ? data.serviceGroup.embedded.rate.shopFlag
                : null
              : null;
            quoteDetail.totalAmount = data.serviceGroup.embedded.rate
              ? data.serviceGroup.embedded.rate.rate
                ? data.serviceGroup.embedded.rate.rate
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.workHeaderId = data.workOrderId;
            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            //this.fixedOperationsService.createQuoteCase(quoteDetail);
          }
        }),
        share()
      )
      .subscribe((data) => {});
  }

  listenForServicesFromToyotaMenu() {
    this.servicesFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.acceptQuoteFromToyotaMenu$,
      this.inspectionService.quoteId$,
      this.inspectionService.workOrderId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, toyotaMenuService, quoteId, workOrderId, appointmentId]) => {
          return { employee, toyotaMenuService, quoteId, workOrderId, appointmentId };
        }),
        distinctUntilKeyChanged('toyotaMenuService'),
        filter((data) => data.toyotaMenuService != null),
        switchMap((data) => {
          return this.serviceLaneFacade
            .quoteLineWithServiceGroupExists(data.toyotaMenuService.serviceGroupId, data.appointmentId)
            .pipe(
              map((quoteExists) => {
                return {
                  employee: data.employee,
                  serviceGroup: data.toyotaMenuService.serviceGroup,
                  quoteExists,
                  quoteId: data.quoteId,
                  workOrderId: data.workOrderId,
                  appointmentId: data.appointmentId,
                  toyotaMenuService: data.toyotaMenuService,
                };
              }),
              take(1),
              distinctUntilKeyChanged('serviceGroup')
            );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            const quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.toyotaMenuService?.rate ? data.toyotaMenuService.rate.opCode : null;
            quoteDetail.opCode = data.toyotaMenuService?.rate ? data.toyotaMenuService.rate.opCode : null;
            quoteDetail.recommendedHours = data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.toyotaMenuService?.rate
              ? data.toyotaMenuService?.rate?.rateTypeId
                ? data?.toyotaMenuService?.rate?.rateTypeId
                : null
              : null;
            if (data.toyotaMenuService?.rate?.rateTypeId === 1) {
              quoteDetail.rate = data?.toyotaMenuService?.rate
                ? data?.toyotaMenuService?.rate?.labourRate
                  ? data?.toyotaMenuService?.rate?.labourRate
                  : null
                : null;
              quoteDetail.labourSubTotal = data?.toyotaMenuService?.rate
                ? data?.toyotaMenuService?.rate?.labourRate
                  ? data?.toyotaMenuService?.rate?.labourRate * data.serviceGroup.recommendedHours
                  : null
                : null;
            } else {
              quoteDetail.rate = data?.toyotaMenuService?.serviceGroupRate
                ? data.toyotaMenuService?.serviceGroupRate
                : null;
              quoteDetail.labourSubTotal = data?.toyotaMenuService?.rate
                ? data?.toyotaMenuService?.rate?.labourRate
                  ? data?.toyotaMenuService?.rate?.labourRate
                  : null
                : null;
            }
            quoteDetail.shopSupplyFlag = data?.toyotaMenuService?.rate
              ? data?.toyotaMenuService?.rate?.shopSupplyFlag
                ? data?.toyotaMenuService?.rate?.shopSupplyFlag
                : null
              : null;
            quoteDetail.totalAmount = data?.toyotaMenuService?.rate
              ? data?.toyotaMenuService?.rate?.total
                ? data?.toyotaMenuService?.rate?.total
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.workHeaderId = data.workOrderId;
            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            this.serviceLaneFacade.approveQuoteLine(quoteDetail);
          }
        }),
        share()
      )
      .subscribe();

    this.declinedFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.declineQuoteFromToyotaMenu$,
      this.inspectionService.quoteId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, declinedData, quoteId, appointmentId]) => {
          return {
            employee,
            serviceGroup: declinedData?.menuItem.serviceGroup,
            declinedData: declinedData,
            quoteId,
            appointmentId,
          };
        }),
        distinctUntilKeyChanged('serviceGroup'),
        filter((data) => data.serviceGroup != null),
        switchMap((data) => {
          return this.serviceLaneFacade.quoteLineWithServiceGroupExists(data.serviceGroup.id, data.appointmentId).pipe(
            map((quoteExists) => {
              return {
                employee: data.employee,
                serviceGroup: data.serviceGroup,
                quoteExists,
                declinedReason: data.declinedData.declinedReason,
                quoteId: data.quoteId,
                appointmentId: data.appointmentId,
                toyotaMenuItem: data.declinedData.menuItem,
              };
            }),
            take(1),
            distinctUntilKeyChanged('serviceGroup')
          );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            const quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.toyotaMenuItem?.rate ? data.toyotaMenuItem.rate.opCode : null;
            quoteDetail.opCode = data.toyotaMenuItem?.rate ? data.toyotaMenuItem.rate.opCode : null;
            quoteDetail.recommendedHours = data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.toyotaMenuItem.rate
              ? data.toyotaMenuItem.rate.rateTypeId
                ? data.toyotaMenuItem.rate.rateTypeId
                : null
              : null;
            if (data.toyotaMenuItem?.rate?.rateTypeId === 1) {
              quoteDetail.rate = data?.toyotaMenuItem?.rate
                ? data?.toyotaMenuItem?.rate?.labourRate
                  ? data?.toyotaMenuItem?.rate?.labourRate
                  : null
                : null;
              quoteDetail.labourSubTotal = data?.toyotaMenuItem?.rate
                ? data?.toyotaMenuItem?.rate?.labourRate
                  ? data?.toyotaMenuItem?.rate?.labourRate * data.serviceGroup.recommendedHours
                  : null
                : null;
            } else {
              quoteDetail.rate = data?.toyotaMenuItem?.serviceGroupRate ? data.toyotaMenuItem?.serviceGroupRate : null;
              quoteDetail.labourSubTotal = data?.toyotaMenuItem?.rate
                ? data?.toyotaMenuItem?.rate?.labourRate
                  ? data?.toyotaMenuItem?.rate?.labourRate
                  : null
                : null;
            }
            quoteDetail.totalAmount = data.toyotaMenuItem.rate
              ? data.toyotaMenuItem.rate.total
                ? data.toyotaMenuItem.rate.total
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;

            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            const declinedQuoteLine: QuoteDetail = new DeepCopyPipe().transform(quoteDetail);
            declinedQuoteLine.declined = data.declinedReason.id;
            this.serviceLaneFacade.declineQuoteLine(declinedQuoteLine);
          }
        }),
        share()
      )
      .subscribe();
  }

  listenForServicesFromHyundaiMenu() {
    this.servicesFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.acceptServiceFromHyundaiMenu$.pipe(map((data) => data?.serviceGroup)),
      this.inspectionService.quoteId$,
      this.inspectionService.workOrderId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, hyundaiMenuService, quoteId, workOrderId, appointmentId]) => {
          return { employee, hyundaiMenuService, quoteId, workOrderId, appointmentId };
        }),
        distinctUntilKeyChanged('hyundaiMenuService'),
        filter((data) => data.hyundaiMenuService != null),
        switchMap((data) => {
          let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          quoteDetailQueryParams.quoteHeaderId = data.quoteId.toString();
          quoteDetailQueryParams.serviceGroupId = data.hyundaiMenuService.serviceGroupId;

          return this.serviceLaneFacade
            .quoteLineWithServiceGroupExists(data.hyundaiMenuService.id, data.appointmentId)
            .pipe(
              map((quoteExists) => {
                return {
                  employee: data.employee,
                  serviceGroup: data.hyundaiMenuService,
                  quoteExists,
                  quoteId: data.quoteId,
                  workOrderId: data.workOrderId,
                  appointmentId: data.appointmentId,
                };
              }),
              take(1),
              distinctUntilKeyChanged('serviceGroup')
            );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(true);
            let quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.recommendedHours = data.serviceGroup.recommendedHours;
            quoteDetail.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.serviceGroupRate.recommendedHours != null
                ? data.serviceGroup.serviceGroupRate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.serviceGroup.serviceGroupRate
              ? data.serviceGroup?.serviceGroupRate?.rateType
                ? data?.serviceGroup?.serviceGroupRate?.rateType
                : null
              : null;
            quoteDetail.rate = data.serviceGroup.serviceGroupRate
              ? data?.serviceGroup?.serviceGroupRate?.rate
                ? data?.serviceGroup?.serviceGroupRate?.rate
                : null
              : null;

            quoteDetail.shopSupplyFlag = data?.serviceGroup?.serviceGroupRate
              ? data?.serviceGroup?.serviceGroupRate?.shopFlag
                ? data?.serviceGroup?.serviceGroupRate?.shopFlag
                : null
              : null;
            quoteDetail.totalAmount = data?.serviceGroup?.serviceGroupRate
              ? data?.serviceGroup?.serviceGroupRate?.rate
                ? data?.serviceGroup?.serviceGroupRate?.rate
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.workHeaderId = data.workOrderId;
            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            this.serviceLaneFacade.approveQuoteLine(quoteDetail);
          }
        }),
        share()
      )
      .subscribe((data) => {});

    this.removeAcceptedServiceFromHyundaiMenuSubscription$ =
      this.serviceLaneFacade.removeAcceptedServiceFromHyundaiMenu$
        .pipe(
          switchMap((serviceGroup) => {
            return this.serviceLaneFacade.quoteLines$.pipe(
              map((qls) => qls.filter((ql) => ql.serviceGroupId === serviceGroup)[0]),
              take(1)
            );
          })
        )

        .subscribe((quoteLine) => {
          if (quoteLine) {
            this.serviceLaneFacade.removeApprovedQuoteLine(quoteLine);
            this.serviceLaneFacade.deleteQuoteLineFromServer(quoteLine);
          }
        });

    this.declinedFromMenuSubscription$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(filter((employee) => employee != null)),
      this.serviceLaneFacade.declineServiceFromHyundaiMenu$,
      this.inspectionService.quoteId$,
      this.inspectionService.workOrderId$,
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        map((appointment) => appointment.id)
      ),
    ])
      .pipe(
        map(([employee, hyundaiMenuService, quoteId, workOrderId, appointmentId]) => {
          return {
            employee,
            serviceGroup: hyundaiMenuService.serviceGroup,
            declinedReason: hyundaiMenuService.declinedReason,
            quoteId,
            workOrderId,
            appointmentId,
          };
        }),
        distinctUntilKeyChanged('serviceGroup'),
        filter((data) => data.serviceGroup != null),
        switchMap((data) => {
          this.hyundaiMenuFacade.declineServiceForHyundaiMenu({
            id: data.serviceGroup.workOrderLocationOpCode,
            declinedReasonId: data.declinedReason,
          });
          let quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters();
          quoteDetailQueryParams.quoteHeaderId = data.quoteId.toString();
          quoteDetailQueryParams.serviceGroupId = data.serviceGroup.id;
          return this.serviceLaneFacade.quoteLineWithServiceGroupExists(data.serviceGroup.id, data.appointmentId).pipe(
            map((quoteExists) => {
              return {
                employee: data.employee,
                serviceGroup: data.serviceGroup,
                quoteExists,
                declinedReason: data.declinedReason,
                quoteId: data.quoteId,
                workOrderId: data.workOrderId,
                appointmentId: data.appointmentId,
              };
            }),
            take(1),
            distinctUntilKeyChanged('serviceGroup')
          );
        }),
        tap((data) => {
          if (!data.quoteExists) {
            this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(true);
            let quoteDetail: QuoteDetail = new QuoteDetail();
            quoteDetail.id = Math.floor(Math.random() * (9999999999999 - 999999999999)) + 999999999999;
            quoteDetail.rooftopId = data.employee.rooftopId;
            quoteDetail.employeeId = data.employee.associateId;
            quoteDetail.associateId = data.employee.associateId;
            quoteDetail.serviceGroupId = data.serviceGroup.id;
            quoteDetail.serviceName = data.serviceGroup.name;
            quoteDetail.labourOpCode = data.serviceGroup.workOrderLocationOpCode;
            quoteDetail.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.serviceGroupRate.recommendedHours != null
                ? data.serviceGroup.serviceGroupRate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;
            quoteDetail.technicianGroupId = data.serviceGroup.technicianGroupId;
            quoteDetail.technicianGroupName = data.serviceGroup.technicianGroupName;
            quoteDetail.technicianId = data.serviceGroup.technicianId;
            quoteDetail.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';
            quoteDetail.rateType = data.serviceGroup.serviceGroupRate
              ? data.serviceGroup.serviceGroupRate.rateType
                ? data.serviceGroup.serviceGroupRate.rateType
                : null
              : null;
            quoteDetail.rate = data.serviceGroup.serviceGroupRate
              ? data.serviceGroup.serviceGroupRate.rate
                ? data.serviceGroup.serviceGroupRate.rate
                : null
              : null;
            quoteDetail.totalAmount = data.serviceGroup.serviceGroupRate
              ? data.serviceGroup.serviceGroupRate.rate
                ? data.serviceGroup.serviceGroupRate.rate
                : null
              : null;
            quoteDetail.lineAddedFromServiceSchedule = 1;
            quoteDetail.expirationDate = new Date();
            quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);

            quoteDetail.quoteHeaderId = data.quoteId;
            quoteDetail.createdFromSourceType = 'SA';
            quoteDetail.createdFromSourceReference = data.appointmentId;
            quoteDetail.workHeaderId = data.workOrderId;
            quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
              ? false
              : true;
            this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
            let declinedQuoteLine: QuoteDetail = new DeepCopyPipe().transform(quoteDetail);
            declinedQuoteLine.declined = data.declinedReason;
            this.serviceLaneFacade.declineQuoteLine(declinedQuoteLine);
          }
        }),
        share()
      )
      .subscribe((data) => {});

    this.updateDeclinedServiceFromHyundaiMenuSubscription$ =
      this.serviceLaneFacade.updateDeclinedServiceFromHyundaiMenu$
        .pipe(
          switchMap((updateData) => {
            return this.serviceLaneFacade.declinedQuoteLines$.pipe(
              map((qls) => {
                return {
                  quoteLineToUpdate: qls.filter(
                    (ql) =>
                      ql.serviceGroupId == updateData.serviceGroup.id &&
                      ql.createdFromSourceType == 'SA' &&
                      ql.createdFromSourceReference == this.activatedRoute.snapshot.queryParams.appointmentId
                  )[0],
                  declinedReason: updateData.declinedReason,
                };
              }),
              take(1)
            );
          })
        )
        .subscribe((updateData) => {
          if (updateData.quoteLineToUpdate) {
            this.serviceLaneFacade.updateDeclinedReasonForQuoteLineInState({
              ...updateData.quoteLineToUpdate,
              declined: updateData.declinedReason,
            });
          }
        });

    this.removeDeclinedServiceFromHyundaiMenuSubscription$ =
      this.serviceLaneFacade.removeDeclinedServiceFromHyundaiMenu$
        .pipe(
          switchMap((serviceGroup) => {
            this.hyundaiMenuFacade.removeDeclinedServiceFromHyundaiMenu(serviceGroup.workOrderLocationOpCode);
            return this.serviceLaneFacade.quoteLines$.pipe(
              map((qls) => qls.filter((ql) => ql.serviceGroupId === serviceGroup.id)[0]),
              take(1)
            );
          })
        )

        .subscribe((quoteLine) => {
          if (quoteLine) {
            this.serviceLaneFacade.removeDeclinedQuoteLine(quoteLine);
            this.serviceLaneFacade.deleteQuoteLineFromServer(quoteLine);
          }
        });
  }

  displayEditCase(caseToEdit: AppointmentCase) {
    this.caseToEdit = caseToEdit;
    this.caseListView = 'edit';
  }

  caseValid(caseStatus: boolean) {}

  selectQuoteLine(quoteLine: QuoteDetail) {
    const dialogRef = this.dialog.open(AcceptQuoteLineDialogComponent, {
      width: '400px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == 'yes') {
        let newCase: AppointmentCase = new AppointmentCase();
        newCase.appointmentId = this.appointmentId;
        newCase.employeeId = '13';
        newCase.opCode = quoteLine.opCode;
        newCase.quoteCaseNumber = quoteLine.sequence;
        newCase.recommendedHours = quoteLine.recommendedHours ? quoteLine.recommendedHours : 0;
        newCase.estimatedTime = quoteLine.recommendedHours ? quoteLine.recommendedHours : 0;
        newCase.warrantySubmissionTypeId = quoteLine.warrantySubmissionTypeId;

        newCase.requestDescription = quoteLine.serviceName;
        newCase.serviceGroupId = quoteLine.serviceGroupId;
        newCase.caseFromQuote = true;
        newCase.technicianId = quoteLine.technicianId;
        newCase.technicianGroupId = quoteLine.technicianGroupId;
        newCase.technicianGroupName = quoteLine.technicianGroupName;

        this.serviceLaneFacade.createAppointmentCaseOnServer(newCase, quoteLine);

        let updatedQuoteLine: QuoteDetail = new DeepCopyPipe().transform(quoteLine);
        updatedQuoteLine.sourceType = 'SA';
        updatedQuoteLine.sourceReferenceId = this.appointmentId;
        this.serviceLaneFacade.updateQuoteLineOnServer(updatedQuoteLine);
      }
    });
  }

  caseSaved(appointmentCase: AppointmentCase) {
    this.caseToEdit = null;
    this.caseListView = 'list';
    if (appointmentCase) {
      this.serviceLaneFacade.updateSelectedAppointmentCaseOnServer(appointmentCase);
    }
  }

  caseCreated(appointmentCase: AppointmentCase) {
    this.caseListView = 'list';
    if (appointmentCase != null) {
      this.serviceLaneFacade.createAppointmentCaseOnServer(appointmentCase, null);
    }
  }

  updateAppointment(appointment: Appointment, appointmentQueryParams: AppointmentQueryParameters) {
    this.serviceLaneFacade.updateAppointmentOnServer(appointment, appointmentQueryParams);
  }

  updateAssociate(associate: Associate) {
    this.serviceLaneFacade.updateAssociateOnServer(associate);
  }

  updateAddress(address: Address) {
    this.serviceLaneFacade.updateAddressOnServer(address);
  }

  private updateLinkedServiceVisit(serviceVisitId: string) {
    if (this.checkInOdometerEdited && this.checkInSavePending) {
      this.pendingServiceVisitId = serviceVisitId;
      return;
    }

    this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(serviceVisitId, this.checkInOdometerEdited);
  }

  private handleCheckInSaveSuccess(updatedAppointment: Appointment) {
    if (
      !this.checkInSavePending ||
      updatedAppointment.id !== Number(this.appointmentId) ||
      !updatedAppointment.customerVerified
    ) {
      return;
    }

    this.checkInSavePending = false;
    this.flushPendingServiceVisit(this.checkInOdometerEdited);
  }

  private handleCheckInSaveError(failedAppointment: Appointment) {
    if (
      !this.checkInSavePending ||
      failedAppointment.id !== Number(this.appointmentId) ||
      !failedAppointment.customerVerified
    ) {
      return;
    }

    this.checkInSavePending = false;
    this.checkInOdometerEdited = false;
    this.serviceLaneFacade.addToast({
      message: 'Check-in verification could not be saved. Please try again.',
      actionText: 'Dismiss',
      options: { duration: 5000 },
    });
    this.flushPendingServiceVisit(false);
  }

  private flushPendingServiceVisit(preserveAdvisorOdometer: boolean) {
    if (!this.pendingServiceVisitId) {
      return;
    }

    const serviceVisitId = this.pendingServiceVisitId;
    this.pendingServiceVisitId = null;
    this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(serviceVisitId, preserveAdvisorOdometer);
  }

  verifyCustomer(
    customerAssociate: Associate,
    address: Address,
    appointment: Appointment,
    overrideOptions: any,
    workOrderId: string,
    odometerEdited = false
  ) {
    this.serviceLaneFacade.updateAssociateOnServer(customerAssociate);
    this.serviceLaneFacade.updateAddressOnServer({
      ...address,
      cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
    });

    combineLatest([
      this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((selectedAppointment) => selectedAppointment != null),
        take(1)
      ),
      this.serviceLaneFacade.vehicle$.pipe(
        filter((selectedVehicle) => selectedVehicle != null),
        take(1)
      ),
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$.pipe(take(1)),
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([latestAppointment, latestVehicle, linkedServiceVisit]) => {
        const linkedVisitForVehicle =
          linkedServiceVisit?.vin === latestVehicle.vin &&
          this.svcVehicleDiagnosticsRootFacade.isWithinADay(
            linkedServiceVisit.checkInDate,
            linkedServiceVisit.checkInTime
          )
            ? linkedServiceVisit
            : null;
        const linkedOdometer = linkedVisitForVehicle ? parseInt(linkedVisitForVehicle.odometer, 10) : null;
        const hasLinkedOdometer = linkedOdometer !== null && !Number.isNaN(linkedOdometer);
        const serviceVisitId = linkedVisitForVehicle?.serviceVisitId || latestAppointment.serviceVisitId;

        let odometerIn = odometerEdited ? appointment.odometerIn : latestAppointment.odometerIn;
        let odometerUpdatedByVehicleDiagnostic = odometerEdited
          ? false
          : latestAppointment.odometerUpdatedByVehicleDiagnostic;

        if (!odometerEdited && hasLinkedOdometer) {
          odometerIn = linkedOdometer;
          odometerUpdatedByVehicleDiagnostic = true;
        }

        const updatedAppointment = new Appointment({
          ...latestAppointment,
          serviceAdvisorId: appointment.serviceAdvisorId,
          odometerIn,
          odometerUpdatedByVehicleDiagnostic,
          serviceVisitId,
          licenseNumber: appointment.licenseNumber,
          transportOptionId: appointment.transportOptionId,
          serviceCode: appointment.serviceCode,
          priorityNumber: appointment.priorityNumber,
          communicatorExcludeFrom: overrideOptions.excludeCommunicator ? true : false,
          customerVerified: true,
        });

        this.serviceLaneFacade.updateAppointmentOnServer(
          updatedAppointment,
          new AppointmentQueryParameters({
            embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
          })
        );

        // Must update vehicle to reflect new odometer and license plate values from appointment check-in verification
        const updatedVehicle = new Vehicle({
          ...latestVehicle,
          currentMileage: odometerIn,
          licensePlateNumber: appointment.licenseNumber,
        });

        this.serviceLaneFacade.updateVehicleOnServer(updatedVehicle);
      });

    if (overrideOptions.messagePreference != null && overrideOptions.messagePreferenceContactInfo != null) {
      this.serviceLaneFacade.createMessageTransactionMessagePreferenceOnServer(
        new MessageTransactionMessagePreference({
          ...new MessageTransactionMessagePreference(),
          messagePreferenceId: overrideOptions.messagePreference,
          messagePreferenceContactInfo: overrideOptions.messagePreferenceContactInfo,
          transactionTypeId: 2,
          transactionId: workOrderId,
        })
      );
    }

    this.verifyCustomerRequired = false;
  }

  skipVerifyCustomer() {
    this.verifyCustomerRequired = false;
  }

  scrollToSection(section: string) {
    document.getElementById(section).scrollIntoView();
  }

  setVerifyCustomerCreateWOButtonEnabled(verifyCustomerCreateWOButtonEnabled: boolean) {
    this.serviceLaneFacade.setVerifyCustomerCreateWOButtonEnabledInState(verifyCustomerCreateWOButtonEnabled);
  }

  ngOnDestroy() {
    this.actionSub.unsubscribe();
    this.updateSub.unsubscribe();
    this.createSub.unsubscribe();
    this.updateSelectedAppointmentSub.unsubscribe();
    this.updateSelectedAppointmentErrorSub.unsubscribe();
    this.deleteSelectedAppointmentCaseSub.unsubscribe();
    this.updateAssociateSub.unsubscribe();
    this.updateAddressSub.unsubscribe();
    this.updateQuoteLineSub.unsubscribe();
    this.updateVehicleSub.unsubscribe();
    this.quoteLineSub.unsubscribe();
    this.addQuoteLineSub.unsubscribe();
    if (this.dashboardSubscription$) {
      this.dashboardSubscription$.unsubscribe();
    }
    this.selectedAppointmentCasesSub.unsubscribe();
    if (this.combinedAppointmentSubscription$) {
      this.combinedAppointmentSubscription$.unsubscribe();
    }
    if (this.servicesFromMenuSubscription$) {
      this.servicesFromMenuSubscription$.unsubscribe();
    }
    if (this.dashboardRetrievalSubscription$) {
      this.dashboardRetrievalSubscription$.unsubscribe();
    }
    if (this.routerStateSubscription$) {
      this.routerStateSubscription$.unsubscribe();
    }
    this.hyundaiMenuFacade.updateServicesForHyundaiMenu([], []);
    this.vehicleMaintenanceService.clearPendingAccepted();
    this.vehicleMaintenanceService.clearPendingDeclined();
    this.vehicleMaintenanceService.clearServicesDue();
    this.vehicleMaintenanceService.clearMaintenanceSchedule();
    this.vehicleMaintenanceService.clearTemplateDetail();
    if (this.declinedFromMenuSubscription$) {
      this.declinedFromMenuSubscription$.unsubscribe();
    }
    if (this.visitSub$) {
      this.visitSub$.unsubscribe();
    }
    this.serviceLaneFacade.dashboardSubject$.next(null);
    if (this.inspectionSavedNotificationSubscription$) {
      this.inspectionSavedNotificationSubscription$.unsubscribe();
    }
    if (this.scheduleSavedNotificationSubscription$) {
      this.scheduleSavedNotificationSubscription$.unsubscribe();
    }
    if (this.authenticatedEmployeeSubscription$) {
      this.authenticatedEmployeeSubscription$.unsubscribe();
    }

    if (this.workOrderSub$) {
      this.workOrderSub$.unsubscribe();
    }

    if (this.createReservedWorkOrderSubscription$) {
      this.createReservedWorkOrderSubscription$.unsubscribe();
    }

    if (this.createQuoteLineOnServer$) {
      this.createQuoteLineOnServer$.unsubscribe();
    }
    if (this.removeAcceptedServiceFromHyundaiMenuSubscription$) {
      this.removeAcceptedServiceFromHyundaiMenuSubscription$.unsubscribe();
    }

    if (this.removeDeclinedServiceFromHyundaiMenuSubscription$) {
      this.removeDeclinedServiceFromHyundaiMenuSubscription$.unsubscribe();
    }

    if (this.updateDeclinedServiceFromHyundaiMenuSubscription$) {
      this.updateDeclinedServiceFromHyundaiMenuSubscription$.unsubscribe();
    }

    if (this.campaignsToCreate$) {
      this.campaignsToCreate$.unsubscribe();
    }

    this.inspectionService.resetService();
    this.serviceLaneFacade.workOrderId$.next(null);

    this.serviceLaneFacade.clearAppointmentState();
    this.svcVehicleDiagnosticsRootFacade.clearVehicleDiagnosticsState();
    this.mediaStateService.clearMediaState();
    this.destroy$.next();
    this.destroy$.complete();
    this.serviceLaneFacade.inspectionDeletedPreviously$.next(false);

    // this.inspectionService.inspection = null;
    // this.inspectionService.inspectionDetails = null;
    // this.inspectionService.inspectionBackup = null;
    //this.inspectionService.isLoading
  }

  createReservedWorkOrder(
    appointment: Appointment,
    vehicle: Vehicle,
    systemControls: any,
    destinationId: string,
    combinedTax: CombinedTax
  ) {
    this.dataLayerService.sendEvent('gaForward', { gaDummy1: 'createReservedWorkOrder', gaDummy2: appointment.id });
    const workOrder: WorkOrder = {
      ...new WorkOrder(),
      appointmentId: appointment.id,
      autoAssign: '1',
      appointmentBookingEmployeeId: appointment.employeeId,
      appointmentDateTime: appointment.dateTime,
      //billToId: -> Need associate_add_ptr API setup.
      body: vehicle.body,
      chassis: '',
      contactPhoneNumber: appointment.phoneNumber,
      currencyId: systemControls['CL_CURRENCY'].value,
      customerId: appointment.customerId,
      createdByVipApp: true,
      dateIn: null,
      deliveryDate: vehicle.deliveryDate,
      electronicWorkOrderFlag: systemControls['CL_ELECTRONIC_WORK_ORDER_FLAG'].value
        ? systemControls['CL_ELECTRONIC_WORK_ORDER_FLAG'].value
        : '0',
      employeeId: appointment.serviceAdvisorId,
      engine: vehicle.engine,
      followUp: '1',
      fordCudbSent: false,
      //globalDiscount:  -> In Xsellerator we only populate this if there a linked Associate seems wrong but *shrug*
      gmDdsSent: false,
      gstWhenCreated: combinedTax.gstTax ? combinedTax.gstTax.gstPercent : null,
      hazardChargeFlag: false,
      includePricingInfoInCustomerMessage: false,
      inspectionComplete: false,
      ivhUsed: false,
      labourRateHour: null,
      last6Vin: vehicle.vin.substr(vehicle.vin.length - 8),
      leaseOperatorId: vehicle.leaseVehicleOperatorId,
      licenseNumber: appointment.licenseNumber,
      phoneReady: '0',
      // eslint-disable-next-line max-len
      //printGlobalDiscount: ->  In Xsellerator we only populate this if there a linked Associate seems wrong but *shrug*
      printInternalFlag: '1',
      printVehicleWarrantyId: vehicle.extendedWarrantyId,
      pstWhenCreated: combinedTax.pstTax ? combinedTax.pstTax.pstPercent : null,
      ready: '0',
      reference: vehicle.stockNumber,
      rooftopId: appointment.rooftopId,
      serialNumber: vehicle.vin,
      series: vehicle.model,
      shopSuppliesPercent: systemControls['CL_SHOP_SUPPLIES_PERCENT'].value,
      statusId: 'R',
      //tagNumber: Handled in the pCreateWorkOrders Script if auto flag is set.
      toyotaCvshSent: false,
      typeId: '1',
      unitOfMeasurementId: vehicle.unitOfMeasureId,
      updatedFlag: '1',
      //useCustomerFlag: --> Need associate_add_ptr API setup.
      vehicleCategory: vehicle.categoryId,
      vehicleId: appointment.vehicleId,
      vehicleMake: vehicle.make,
      vehicleStatusId: vehicle.statusCode,
      vehicleTypeId: vehicle.embedded['type'].classId,
      walkaroundComplete: false,
      warrantyAccepted: '0',
      warrantyAuditDone: '0',
      warrantyReportFlag: '1',
      year: vehicle.year && vehicle.year.toString() != '1900' ? vehicle.year.toString() : null,
    };

    this.apiService.post<WorkOrder>(`v/1/fixed-operations/work-orders`, workOrder).subscribe((workOrder: WorkOrder) => {
      const appointmentQueryParams: AppointmentQueryParameters = {
        id: appointment.id.toString(),
        embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
      };
      this.inspectionService.triggerInspectionLogic.next(workOrder.id);
      this.serviceLaneFacade.getSelectedAppointment(appointmentQueryParams);
      this.inspectionService.getQuote(workOrder.id).then((quote) => {
        this.inspectionService.workOrderId$.next(workOrder.id);
        this.serviceLaneFacade.workOrderId$.next(workOrder.id);
        this.inspectionService.quoteId$.next(quote.QuoteId);
        this.inspectionService.workOrderId = workOrder.id;
        this.inspectionService.quoteId = quote.QuoteId;
        this.inspectionService.vehicleId = this.route.snapshot.params.vehicleId;
        this.inspectionService.appointmentId = this.route.snapshot.params.appointmentId;

        this.mediaStateService.setMediaModelKey({
          vehicleId: this.inspectionService.vehicleId,
          appointmentId: this.inspectionService.appointmentId,
          workOrderId: this.inspectionService.workOrderId,
          quoteId: this.inspectionService.quoteId,
          destinationId: destinationId,
          customerFacing: true,
          key: 'appointmentCase',
        });
        //this.listenForServicesFromMenu();
        this.inspectionSavedNotificationSubscription$ = this.inspectionService.inspectionSavedNotification.subscribe(
          () => {
            this.serviceLaneFacade.getQuoteLinesFromServer({
              quoteHeaderId: quote.QuoteId.toString(),
              embed: 'employeeAssociate,serviceGroup',
            });
          }
        );
        // eslint-disable-next-line max-len
        this.scheduleSavedNotificationSubscription$ =
          this.vehicleMaintenanceService.scheduleSavedNotification.subscribe(() => {
            this.serviceLaneFacade.getQuoteLinesFromServer({
              quoteHeaderId: quote.QuoteId.toString(),
              embed: 'employeeAssociate,serviceGroup',
            });
          });
      });
    });
  }

  toggleTireFinder(toggle: boolean) {
    this.toggleTireFinderWidget = toggle;
    this.refreshQuoteLines();
  }

  refreshQuoteLines() {
    this.inspectionService.quoteId$
      .pipe(
        filter((quoteId): quoteId is string => !!quoteId),
        take(1)
      )
      .subscribe((quoteId) => {
        this.serviceLaneFacade.getQuoteLinesFromServer(
          new QuoteDetailQueryParameters({ quoteHeaderId: quoteId, embed: 'employeeAssociate,serviceGroup' })
        );
      });
  }

  hideTireFinderIfEnabled() {
    if (this.toggleTireFinderWidget) {
      this.toggleTireFinderWidget = false;
    }
  }
}
